import React, { Component } from "react";
import "./isp.css";
import { post } from "aws-amplify/api";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import {
  getCustomFees,
  getManagementFees,
  getManagementFeeTiers,
  getS3File,
} from "../../services/submissionService";
import CollapsibleSection from "./CollapsibleSection";
import ISPClientProfile from "./ispClientProfile";
import ISPAccountInfo from "./ispAccountInfo";
import RiskQuestionnaire, {
  CAPACITY_QUESTIONS,
  WILLINGNESS_QUESTIONS,
  getPortfolioRiskCategory,
  portfolioMatchesRiskCategory,
} from "./RiskQuestionnaire";
import ISPFeeSchedule from "./ispFeeSchedule";
import ISPConfirm from "./ispConfirm";
import {
  getFusionFeeKey as buildFusionFeeKey,
  getFusionFeeValidation,
  resolveFusionSubFeeEntry,
  resolveFusionSubFees as resolveFusionFeeTable,
  sortFeeTiers,
  sortManagementFeeTiers,
  validateManagementFeeTable,
} from "./fusionFeeUtils";
import {
  calculateFusionWeightedRiskScore,
  getEligibleFusionStrategies,
  getFusionAllocationDisplayName,
  getProgramDisplayName,
  isPrivateClientStrategyAlias,
  isStrategyAvailableForAdvisors,
} from "../../utils/proposalUtils";
import {
  buildManagementFeeTierOptions,
  collapseTierLabel,
  normalizeTierKey,
  resolveManagementFeeTierSelection,
  splitEvolutionTiers,
} from "./managementFeeTierUtils";
import { normalizeIarFeeTiered } from "./iarFeeTieredUtils";
import {
  getIarFeeSchedules,
  isCustomFeeResponseAuthorizedForAdvisor,
  reconcileTieredRatesForSchedule,
  resolveIarFeeSchedule,
} from "./iarFeeScheduleUtils";
import {
  isElementsStrategyName,
  shouldSuppressElementsPathStrategy,
} from "./elementsFeeUtils";
import {
  getIspFundingThreshold,
  isInvestmentAmountWithinLimits,
  resolveStrategyInvestmentLimits,
} from "./ispFundingThresholds";
import {
  MMMS_SELECTION_CONFIG,
  canonicalizeMmmsFundSelection,
  getMmmsFundBondType,
  isManagedMoneyMarketStrategyName,
} from "./mmmsSelectionUtils";
import {
  areFeeUpdateAccountsComplete,
  buildFeeUpdateLegacyFields,
  createEmptyFeeUpdateAccount,
  getFeeUpdateAccountReferenceIssue,
  getFeeUpdateStrategyCompatibilityIssue,
  normalizeFeeUpdateAccounts,
} from "./feeUpdateAccountUtils";
import {
  getBondPreferencePayload,
  getConditionalStateReset,
  getIspRegistrationTypes,
  getRegistrationDetailValidationIssue,
  getRegistrationRules,
  getRequestCapabilities,
  exceedsSoloKParticipantLimit,
  isStrategyChangeWorkflowAction,
  isStrategyEligibleForRequest,
  isValidIarPercentage,
  normalizeRegistrationDetails,
  shouldAskSoloKSpouseQuestion,
} from "./ispBusinessRules";
import { getMmmsStrategyDescription } from "./mmmsDisclosure";
import { isIspRegistrationAvailable } from "./ispRegistrationOptions";
import {
  arePcsRegistrationRowsComplete,
  buildPcsRegistrationPayload,
  isPcsRegistrationStrategy,
  normalizePcsRegistrationRows,
  supportsPcsRegistrationType,
} from "./pcsRegistrationUtils";
import {
  areStandardRegistrationRowsComplete,
  buildStandardRegistrationPayload,
  createStandardRegistrationFields,
  createStandardRegistrationRow,
  isStandardRegistrationStrategy,
  normalizeStandardRegistrationRows,
  supportsStandardRegistrationType,
} from "./standardRegistrationUtils";

export const ZERO_FEE_CONFIRMATION_MESSAGE =
  "You have entered an IAR fee of 0%. Please confirm this selection is correct before submitting the ISP.";

export const ISP_PREFILL_RESET_STORAGE_KEY = "vicus.isp.prefillReset";

function getStoredPrefillReset() {
  const fallback = { resetAt: "", generation: 0 };
  try {
    const stored = JSON.parse(
      localStorage.getItem(ISP_PREFILL_RESET_STORAGE_KEY) || "null",
    );
    const resetAt = String(stored?.resetAt || "");
    const generation = Number(stored?.generation);
    return {
      resetAt: Number.isNaN(Date.parse(resetAt)) ? "" : resetAt,
      generation:
        Number.isInteger(generation) && generation >= 0 ? generation : 0,
    };
  } catch (error) {
    return fallback;
  }
}

// All strategies now use the same questions with max 100.
function computeRiskScores(capAnswers, willAnswers) {
  const capacityTotal = CAPACITY_QUESTIONS.reduce((sum, q) => sum + (capAnswers[q.id] || 0), 0);
  const willingnessTotal = WILLINGNESS_QUESTIONS.reduce((sum, q) => sum + (willAnswers[q.id] || 0), 0);
  const combinedScore = capacityTotal + willingnessTotal;
  const riskCategory = combinedScore <= 35 ? "Conservative" : combinedScore <= 65 ? "Moderate" : "Growth";
  return { capacityTotal, willingnessTotal, combinedScore, riskCategory };
}

// Normalize either an ISO date (YYYY-MM-DD) or a legacy localized M/D/YYYY
// (left over from earlier submissions that were saved in display format) back
// to the YYYY-MM-DD shape that <input type="date"> requires. Anything we can't
// parse is dropped to "" so it can't crash the date input's min/max math.
function toIsoDate(dateStr) {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const m = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const mm = m[1].padStart(2, "0");
    const dd = m[2].padStart(2, "0");
    return `${m[3]}-${mm}-${dd}`;
  }
  return "";
}

// Narrow a list of portfolio names to those matching a chosen model selection.
// Convention (shared with getAvailableBondTypesForModel): the model option name
// minus a trailing " Strategy" is a prefix of — or equal to — the portfolio
// name (e.g. "Core Bond Strategy" matches "Core Bond - Taxable"). With no model
// selected the full list passes through unchanged.
function narrowBondsByModel(bonds, modelSelection) {
  const key = (modelSelection || "").replace(/\s+Strategy$/i, "").trim().toLowerCase();
  if (!key) return bonds;
  return bonds.filter((b) => {
    const lower = b.toLowerCase();
    return (
      lower === key ||
      lower.startsWith(`${key} `) ||
      lower.startsWith(`${key}-`) ||
      lower.startsWith(`${key} -`)
    );
  });
}

function getEmptySoloKQuestionnaire() {
  return {
    sponsoringBusinessName: "",
    ownerCount: "",
    employeeCount: "",
    secondParticipantIsSpouse: "",
    anticipatesEmployees: "",
    planType: "",
    existingPlanDocumentsProvided: false,
    ownershipEvidence: [],
    certificationNoEmployees: false,
    certificationContributionLimits: false,
  };
}

function isSoloKRegistrationValue(value) {
  const raw = String(value || "").toLowerCase();
  return (
    raw.includes("solo(k)") ||
    raw.includes("solo k") ||
    raw.includes("solo 401")
  );
}

function isEstateRegistrationValue(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "estate" || normalized.startsWith("estate ");
}

function getFirstSavedAccountLast4(data = {}) {
  const accountNumber = String(data.accountNumber || "")
    .split(",")
    .map((value) => value.trim())
    .find(Boolean);
  if (accountNumber) return accountNumber;

  if (!Array.isArray(data.accountNumbers)) return "";
  return String(
    data.accountNumbers.find((value) => String(value || "").trim()) || "",
  ).trim();
}

async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    if (session.tokens?.idToken) {
      return { Authorization: `Bearer ${session.tokens.idToken.toString()}` };
    }
    return {};
  } catch (error) {
    console.log("Error getting auth headers:", error);
    return {};
  }
}

function getJointClientIdentity(client = {}) {
  return String(client.id || [
    client.clientName,
    client.ssnLast4,
    client.firstName,
    client.lastName,
  ].join("|")).trim();
}

function mergeJointClients(...clientLists) {
  const seen = new Set();
  return clientLists.flat().filter((client) => {
    const identity = getJointClientIdentity(client);
    if (!identity || seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
}

class ISPForm extends Component {
  state = {
    // Section expansion
    expandedSection: "1",

    // VIC-000043 — retain the browser-local reset point so proposal-backed
    // suggestions restart without changing any values in the active ISP.
    prefillResetAt: getStoredPrefillReset().resetAt,
    prefillGeneration: getStoredPrefillReset().generation,
    prefillResetNotice: false,

    // Section 1: Client Information
    advisorName: "",
    additionalAdvisors: [], // array of name strings for secondary advisors
    requestType: "",
    existingRequestAction: "",
    accountNumber: "",
    accountNumbers: [""],
    feeUpdateAccounts: [createEmptyFeeUpdateAccount()],
    // T1 — round 4: advisor attests whether a Client Profile + IMA is being
    // submitted alongside this ISP. "No" surfaces a long attestation paragraph
    // on the PDF (built by api-isp-request.py) just above the signature block.
    clientProfileImaSubmitted: "",
    householding: "",
    // T8 — round 4: householding reference split into separate name + last-4
    // inputs (both required when householding is Yes). The legacy single
    // householdRef field is preserved as a fallback so older drafts/submissions
    // restored from sessionStorage don't blow up.
    householdRefName: "",
    householdRefLast4: "",
    householdRef: "",
    householdReason: "",
    householdReasonType: "",
    householdReasonOther: "",
    jointClients: [],
    deferredJointClients: [],
    showClientDialog: false,
    dialogEditIndex: -1,
    dialogCopySource: -1,

    // Section 2: Account Information
    custodian: "",
    strategy: "",
    bondType: "Taxable",
    bondTypePreferenceProvided: false,
    modelSelection: "",
    registrations: [
      { registration: "registration0", value: "", percentage: "100" },
    ],
    initialInvestmentAmount: "",
    accountNetWorthPct: "",
    accountIncomeNeed: "",
    accountIncomeAmount: "",
    accountIncomeStartDate: "",
    accountIncomeEndDate: "",
    accountIncomeFrequency: "",
    accountGoal: "",
    accountGoalOther: "",
    sourceOfFunds: [],
    sourceOfFundsText: {},
    soloKQuestionnaire: getEmptySoloKQuestionnaire(),
    // T15 — round 4: when registrations[0] is a UGMA/UTMA registration type,
    // capture the minor's name and their relationship to the custodian.
    // Cleared when the registration changes to a non-UGMA/UTMA type.
    minorName: "",
    relationshipToCustodian: "",
    custodialAdultName: "",
    custodianFullName: "",
    relationshipToPrimary: "",
    trustName: "",
    businessName: "",
    businessEinLast4: "",
    decedentName: "",
    decedentSsnLast4: "",
    relationshipToExecutor: "",
    fusionRegistrationType: "Single",
    pcsRegistrationType: "Single",
    standardRegistrationType: "Single",
    fusionRebalanceType: "Automatic",
    // Path (graduation) strategy bond choice — independent of the
    // primary `bondType`. Only meaningful when the selected Elements strategy's
    // path target offers both Taxable and Municipal.
    pathBondType: "",

    // Section 3: Risk Questionnaire
    capacityAnswers: {},
    willingnessAnswers: {},
    capacityTotal: 0,
    willingnessTotal: 0,
    combinedScore: 0,
    riskCategory: "",
    selectedPortfolio: "",
    portfolioOverride: false,
    overrideReason: "",
    overrideOtherText: "",

    // Section 4: Fees
    customFees: null,
    customFeesLoading: false,
    customFeesError: "",
    managementFees: null,
    managementFeesLoading: false,
    feeUpdateStrategyFeeBlocks: [],
    feeUpdateStrategyFeesLoading: false,
    feeUpdateStrategyFeesError: "",
    iarFeeTier: "",
    selectedManagementFeeTierKey: "",
    availableManagementFeeTiers: [],
    availableManagementFeeTierOptions: [],
    iarFeeType: "",
    iarFeeFlat: "",
    iarFeeTiered: [],
    // Path-stage IAR fee (post-graduation). Used only when the selected
    // strategy is flat (`flatFee` on its mgmt-fees row) AND its pathStrategy
    // resolves to a real fee row. Mirrors iarFeeType/iarFeeFlat/iarFeeTiered.
    pathIarFeeType: "Fixed Percentage",
    pathIarFeeFlat: "",
    pathIarFeeTiered: [],
    iarFeeScheduleType: "",
    iarFeeWaiver: null, // { confirmedAt, confirmedBy, zeroFields }
    feesSectionVisited: false,

    // Fusion blended fee allocations
    fusionAllocations: [],
    fusionSubFees: {},
    fusionSubFeesLoading: false,
    fusionFeeValidation: { isValid: true, issues: [] },

    // Zero fee modal
    showZeroFeeModal: false,
    zeroFeeConfirmationPending: false,

    // Q5 chart image loaded from S3
    riskChartUrl: "",

    // Generic confirm modal
    confirmModal: null, // { title, message, onConfirm }

    // Section 5: Review & Submit
    isSubmitting: false,
    showSuccess: false,
    submitError: "",
    proposalPrefillInfo: "",
    isEditing: false,
  };

  _mounted = true;

  // Monotonic tokens guarding async fee fetches. A tier/strategy change can
  // leave an earlier fetch in flight; without these, a slow Standard-tier
  // response can land after the newer Tier B response and clobber state.
  _managementFeesRequestId = 0;
  _fusionSubFeesRequestId = 0;
  _fusionFeeRequests = new Map();
  _feeUpdateStrategyFeesRequestId = 0;
  _customFeesRequestId = 0;
  _restoredManagementFeeTierSelection = "";

  componentDidMount() {
    document.addEventListener("keydown", this.handleGlobalKeyDown);

    // Auto-set default advisor if lists are already loaded
    const defaultAdvisor = this.props.lists?.userInfo?.DefaultAdvisor;
    if (defaultAdvisor && !this.state.advisorName) {
      this.setState({ advisorName: defaultAdvisor });
    }

    // Edit mode takes priority over prefill — only one should run
    if (sessionStorage.getItem("editISPData")) {
      this.checkForEditData();
    } else {
      this.checkForProposalPrefill();
    }

    // Load Q5 risk chart image from S3
    this.loadRiskChart();
  }

  checkForEditData = () => {
    try {
      const raw = sessionStorage.getItem("editISPData");
      if (!raw) return;

      const editDateTime = sessionStorage.getItem("editISPDateTime") || "";
      sessionStorage.removeItem("editISPData");
      sessionStorage.removeItem("editISPDateTime");

      const data = JSON.parse(raw);
      const updates = {
        isEditing: !!editDateTime,
        proposalDateTime: editDateTime || undefined,
      };

      // Section 1: Client Information
      if (data.advisorName) updates.advisorName = data.advisorName;
      if (data.requestType) updates.requestType = data.requestType;
      if (data.existingRequestAction) updates.existingRequestAction = data.existingRequestAction;
      const restoringFeeUpdate =
        data.requestType === "Existing" &&
        data.existingRequestAction === "Fee Update";
      if (data.requestType === "Existing" && !restoringFeeUpdate) {
        const accountLast4 = getFirstSavedAccountLast4(data);
        updates.accountNumber = accountLast4;
        updates.accountNumbers = accountLast4 ? [accountLast4] : [""];
        updates.feeUpdateAccounts = [createEmptyFeeUpdateAccount()];
      }
      if (data.clientProfileImaSubmitted) updates.clientProfileImaSubmitted = data.clientProfileImaSubmitted;
      if (data.householding) updates.householding = data.householding;
      if (data.householdRefName) updates.householdRefName = data.householdRefName;
      if (data.householdRefLast4) updates.householdRefLast4 = data.householdRefLast4;
      // Older drafts only carry householdRef (the legacy combined last-4
      // field). Restore it as the new last-4 input so saved-but-unmigrated
      // forms don't drop the value silently.
      if (data.householdRef) {
        updates.householdRef = data.householdRef;
        if (!data.householdRefLast4) updates.householdRefLast4 = data.householdRef;
      }
      if (data.householdReason) updates.householdReason = data.householdReason;
      if (data.householdReasonType) {
        updates.householdReasonType = data.householdReasonType;
      } else if (data.householdReason === "This is the only account in the household.") {
        updates.householdReasonType = "Only account";
      } else if (data.householdReason) {
        updates.householdReasonType = "Other";
        updates.householdReasonOther = data.householdReason;
      }
      if (data.householdReasonOther) updates.householdReasonOther = data.householdReasonOther;
      if (data.jointClients && Array.isArray(data.jointClients)) updates.jointClients = data.jointClients;
      if (data.additionalAdvisors && Array.isArray(data.additionalAdvisors)) updates.additionalAdvisors = data.additionalAdvisors;

      // Section 2: Account Information
      if (data.custodian) updates.custodian = data.custodian;
      if (data.strategy) updates.strategy = data.strategy;
      const restoringRiskToleranceUpdate =
        data.requestType === "Existing" &&
        data.existingRequestAction === "Risk Tolerance Update";
      const restoredBondTypePreferenceProvided =
        data.bondTypePreferenceProvided === true ||
        data.bondTypePreferenceProvided === "true";
      const restoredBondType = ["Taxable", "Municipal"].includes(data.bondType)
        ? data.bondType
        : "";
      if (restoringRiskToleranceUpdate) {
        updates.bondTypePreferenceProvided =
          restoredBondTypePreferenceProvided && !!restoredBondType;
        updates.bondType = updates.bondTypePreferenceProvided
          ? restoredBondType
          : "";
      } else if (data.bondType) {
        updates.bondType = data.bondType;
      }
      if (data.modelSelection && !restoringRiskToleranceUpdate) {
        updates.modelSelection = data.modelSelection;
      }
      const restoringFusion = (data.strategy || "")
        .toLowerCase()
        .includes("fusion");
      if (restoringFusion) {
        const fusionRegistrationType =
          data.fusionRegistrationType === "Multi" ? "Multi" : "Single";
        updates.fusionRegistrationType = fusionRegistrationType;
        updates.fusionRebalanceType =
          fusionRegistrationType === "Multi"
            ? "Manual"
            : data.fusionRebalanceType === "Manual"
              ? "Manual"
              : "Automatic";
      }
      if (data.registrations && Array.isArray(data.registrations)) updates.registrations = data.registrations;
      if (
        data.requestType === "Existing" &&
        data.existingRequestAction === "Fee Update"
      ) {
        const feeUpdateAccounts = normalizeFeeUpdateAccounts(data, {
          registrationTypes: this.props.lists?.registrationTypes || [],
        });
        const legacyFields = buildFeeUpdateLegacyFields(feeUpdateAccounts);
        updates.feeUpdateAccounts = feeUpdateAccounts;
        updates.accountNumbers = legacyFields.accountNumbers;
        updates.registrations = legacyFields.registrations;
        updates.strategy = legacyFields.strategy;
        updates.feeUpdateStrategyFeeBlocks = Array.isArray(
          data.feeUpdateStrategyFeeBlocks,
        )
          ? data.feeUpdateStrategyFeeBlocks
          : [];
      }
      const restoredPcsStrategy = restoringFeeUpdate
        ? (updates.feeUpdateAccounts || []).length > 0 &&
          (updates.feeUpdateAccounts || []).every((account) =>
            isPcsRegistrationStrategy(
              account?.strategy,
              this.props.lists?.proposalTypes || [],
            ),
          )
        : isPcsRegistrationStrategy(
            data.strategy,
            this.props.lists?.proposalTypes || [],
          );
      if (
        restoredPcsStrategy &&
        supportsPcsRegistrationType(data.requestType, data.existingRequestAction)
      ) {
        const savedPcsRows = Array.isArray(data.pcsRegistrations)
          ? data.pcsRegistrations.map((registration, index) => ({
              registration: `registration${index}`,
              accountOwner: registration?.accountOwner || "",
              value:
                registration?.value || registration?.registration || "",
              percentage: "100",
            }))
          : normalizePcsRegistrationRows(data.registrations || []);
        const inferredCount = restoringFeeUpdate
          ? (updates.feeUpdateAccounts || []).length
          : savedPcsRows.length;
        const pcsRegistrationType =
          data.pcsRegistrationType === "Multi" ||
          (!data.pcsRegistrationType && inferredCount > 1)
            ? "Multi"
            : "Single";
        updates.pcsRegistrationType = pcsRegistrationType;
        if (!restoringFeeUpdate && pcsRegistrationType === "Multi") {
          updates.registrations =
            savedPcsRows.length > 0
              ? savedPcsRows
              : normalizePcsRegistrationRows(this.state.registrations);
          }
      }
      const restoredStandardStrategy = restoringFeeUpdate
        ? (updates.feeUpdateAccounts || []).length > 0 &&
          (updates.feeUpdateAccounts || []).every((account) =>
            isStandardRegistrationStrategy(
              account?.strategy,
              this.props.lists?.proposalTypes || [],
            ),
          )
        : isStandardRegistrationStrategy(
            data.strategy,
            this.props.lists?.proposalTypes || [],
          );
      if (
        restoredStandardStrategy &&
        supportsStandardRegistrationType(
          data.requestType,
          data.existingRequestAction,
        )
      ) {
        const savedRows = Array.isArray(data.standardRegistrations)
          ? data.standardRegistrations
          : data.registrations || [];
        const inferredCount = restoringFeeUpdate
          ? (updates.feeUpdateAccounts || []).length
          : savedRows.length;
        const registrationType =
          data.standardRegistrationType === "Multi" ||
          (!data.standardRegistrationType && inferredCount > 1)
            ? "Multi"
            : "Single";
        updates.standardRegistrationType = registrationType;
        if (!restoringFeeUpdate && registrationType === "Multi") {
          updates.registrations = normalizeStandardRegistrationRows(
            savedRows,
            data,
          );
        }
      }
      if (data.initialInvestmentAmount) updates.initialInvestmentAmount = data.initialInvestmentAmount;
      if (data.accountNetWorthPct) updates.accountNetWorthPct = data.accountNetWorthPct;
      if (data.accountIncomeNeed) updates.accountIncomeNeed = data.accountIncomeNeed;
      if (data.accountIncomeAmount) updates.accountIncomeAmount = data.accountIncomeAmount;
      if (data.accountIncomeStartDate) updates.accountIncomeStartDate = toIsoDate(data.accountIncomeStartDate);
      if (data.accountIncomeEndDate) updates.accountIncomeEndDate = toIsoDate(data.accountIncomeEndDate);
      if (data.accountIncomeFrequency) updates.accountIncomeFrequency = data.accountIncomeFrequency;
      if (data.accountGoal) updates.accountGoal = data.accountGoal;
      if (data.accountGoalOther) updates.accountGoalOther = data.accountGoalOther;
      if (data.sourceOfFunds && Array.isArray(data.sourceOfFunds)) updates.sourceOfFunds = data.sourceOfFunds;
      if (data.sourceOfFundsText) updates.sourceOfFundsText = data.sourceOfFundsText;
      if (data.soloKQuestionnaire) {
        updates.soloKQuestionnaire = {
          ...getEmptySoloKQuestionnaire(),
          ...data.soloKQuestionnaire,
          ownershipEvidence: Array.isArray(data.soloKQuestionnaire.ownershipEvidence)
            ? data.soloKQuestionnaire.ownershipEvidence
            : [],
        };
      }
      if (data.minorName) updates.minorName = data.minorName;
      if (data.relationshipToCustodian) updates.relationshipToCustodian = data.relationshipToCustodian;
      if (data.custodialAdultName) updates.custodialAdultName = data.custodialAdultName;
      if (data.custodianFullName) updates.custodianFullName = data.custodianFullName;
      if (data.relationshipToPrimary) {
        updates.relationshipToPrimary = data.relationshipToPrimary;
      }
      if (data.trustName) updates.trustName = data.trustName;
      if (data.businessName) updates.businessName = data.businessName;
      if (data.businessEinLast4) updates.businessEinLast4 = data.businessEinLast4;
      if (data.decedentName) updates.decedentName = data.decedentName;
      if (data.decedentSsnLast4) updates.decedentSsnLast4 = data.decedentSsnLast4;
      if (data.relationshipToExecutor) {
        updates.relationshipToExecutor = data.relationshipToExecutor;
      }

      // Section 3: Risk Questionnaire — restore original answers if available
      const restoredRegistration = updates.registrations?.[0]?.value || "";
      if (!restoringFeeUpdate) {
        const restoredRegistrationDetails = normalizeRegistrationDetails(
          data,
          restoredRegistration,
          this.props.lists?.registrationTypes || [],
        );
        Object.assign(updates, restoredRegistrationDetails, {
          // Compatibility mirror for saved ISPs created before item 4.
          custodialAdultName:
            restoredRegistrationDetails.custodianFullName || "",
        });
      }
      const restoredRules = getRegistrationRules(
        restoredRegistration,
        updates.custodian || "",
      );
      if (!restoredRules.isMinorRegistration) {
        updates.custodialAdultName = "";
        updates.custodianFullName = "";
        updates.relationshipToPrimary = "";
      }
      if (!restoredRules.asksTrustName) {
        updates.trustName = "";
      }
      const restoredAccountHolderState = {
        ...this.state,
        ...updates,
      };
      if (
        !this.allowsSecondAccountHolderForState(restoredAccountHolderState) &&
        Array.isArray(updates.jointClients)
      ) {
        const primary =
          updates.jointClients.find((client) => client?.isPrimary) ||
          updates.jointClients[0];
        updates.jointClients = primary
          ? [{ ...primary, isPrimary: true }]
          : [];
      }
      if (!restoredRules.allowsHouseholding) {
        Object.assign(updates, {
          householding: "",
          householdRefName: "",
          householdRefLast4: "",
          householdRef: "",
          householdReason: "",
          householdReasonType: "",
          householdReasonOther: "",
        });
      }

      if (data.riskScores) {
        const rs = data.riskScores;
        if (rs.capacityAnswers) updates.capacityAnswers = rs.capacityAnswers;
        if (rs.willingnessAnswers) updates.willingnessAnswers = rs.willingnessAnswers;
        if (rs.capacityTotal != null) updates.capacityTotal = rs.capacityTotal;
        if (rs.willingnessTotal != null) updates.willingnessTotal = rs.willingnessTotal;
        if (rs.combinedScore != null) updates.combinedScore = rs.combinedScore;
        if (rs.riskCategory) updates.riskCategory = rs.riskCategory;
        if (rs.selectedPortfolio) updates.selectedPortfolio = rs.selectedPortfolio;
        if (rs.portfolioOverride != null) updates.portfolioOverride = rs.portfolioOverride;
        if (rs.overrideReason) updates.overrideReason = rs.overrideReason;
        if (rs.overrideOtherText) updates.overrideOtherText = rs.overrideOtherText;
      }

      // Section 4: Fees
      const savedManagementFeeTier = data.managementFeeTierKey || data.iarFeeTier || "";
      if (savedManagementFeeTier) {
        this._restoredManagementFeeTierSelection = savedManagementFeeTier;
        updates.iarFeeTier = this.toTierSelectionValue(savedManagementFeeTier);
        updates.selectedManagementFeeTierKey = normalizeTierKey(savedManagementFeeTier);
      }
      if (data.iarFeeType) updates.iarFeeType = data.iarFeeType;
      if (data.iarFeeFlat !== undefined && data.iarFeeFlat !== null) {
        updates.iarFeeFlat = String(data.iarFeeFlat);
      }
      if (Array.isArray(data.iarFeeTiered)) updates.iarFeeTiered = data.iarFeeTiered;
      if (data.iarFeeScheduleType) updates.iarFeeScheduleType = data.iarFeeScheduleType;
      if (data.pathIarFeeType) updates.pathIarFeeType = data.pathIarFeeType;
      if (data.pathIarFeeFlat !== undefined && data.pathIarFeeFlat !== null) {
        updates.pathIarFeeFlat = String(data.pathIarFeeFlat);
      }
      if (Array.isArray(data.pathIarFeeTiered)) {
        updates.pathIarFeeTiered = data.pathIarFeeTiered;
      }
      if (data.fusionAllocations) updates.fusionAllocations = data.fusionAllocations;

      if ((data.strategy || "").toLowerCase().includes("fusion")) {
        const normalizedFusionState = this.normalizeLegacyFusionFeeState(
          savedManagementFeeTier,
          data.fusionAllocations,
          updates.fusionRegistrationType,
        );
        if (normalizedFusionState.iarFeeTier) {
          updates.iarFeeTier = normalizedFusionState.iarFeeTier;
          updates.selectedManagementFeeTierKey = normalizedFusionState.selectedManagementFeeTierKey;
        } else if (String(data.iarFeeTier || "").toLowerCase() === "blended") {
          updates.iarFeeTier = "";
          updates.selectedManagementFeeTierKey = "";
        }
        updates.fusionAllocations = normalizedFusionState.fusionAllocations;
      }

      if (isManagedMoneyMarketStrategyName(data.strategy)) {
        const selection = canonicalizeMmmsFundSelection(
          data.modelSelection || data.riskScores?.selectedPortfolio,
          data.bondType,
        );
        if (selection) {
          updates.modelSelection = selection;
          updates.selectedPortfolio = selection;
          updates.bondType = getMmmsFundBondType(selection);
          updates.portfolioOverride = false;
          updates.overrideReason = "";
          updates.overrideOtherText = "";
        }
      }

      if (Object.keys(updates).length > 0) {
        this.setState(updates);
      }
    } catch (e) {
      console.error("Error loading ISP edit data:", e);
      sessionStorage.removeItem("editISPData");
      sessionStorage.removeItem("editISPDateTime");
    }
  };

  normalizeProposalPrefillRegistrations = (registrations) => {
    let rawRegistrations = registrations;

    if (typeof registrations === "string") {
      try {
        const parsed = JSON.parse(registrations);
        rawRegistrations = Array.isArray(parsed) ? parsed : registrations;
      } catch {
        rawRegistrations = registrations;
      }
    }

    const rows = Array.isArray(rawRegistrations)
      ? rawRegistrations
      : [rawRegistrations];

    return rows
      .map((registration, index) => {
        if (typeof registration === "string") {
          return {
            registration: `registration${index}`,
            value: registration,
            percentage: index === 0 ? "100" : "",
          };
        }

        if (!registration || typeof registration !== "object") return null;

        return {
          registration: registration.registration || `registration${index}`,
          value: registration.value || registration.name || registration.label || "",
          percentage:
            registration.percentage !== undefined
              ? registration.percentage
              : index === 0
                ? "100"
                : "",
        };
      })
      .filter((registration) => registration?.value);
  };

  checkForProposalPrefill = () => {
    try {
      const raw = sessionStorage.getItem("proposalToISPData");
      if (!raw) return;
      sessionStorage.removeItem("proposalToISPData");

      const data = JSON.parse(raw);
      const updates = {};

      if (data.advisorName) updates.advisorName = data.advisorName;
      if (data.strategy) updates.strategy = data.strategy;
      if (data.accountValue) updates.initialInvestmentAmount = data.accountValue;
      if (data.bondType) updates.bondType = data.bondType;
      if (data.riskTolerance) updates.selectedPortfolio = data.riskTolerance;
      if (isManagedMoneyMarketStrategyName(data.strategy)) {
        const selection = canonicalizeMmmsFundSelection(
          data.modelSelection || data.riskTolerance,
          data.bondType,
        );
        if (selection) {
          updates.modelSelection = selection;
          updates.selectedPortfolio = selection;
          updates.bondType = getMmmsFundBondType(selection);
        }
      }

      // Handle registration — may be a JSON array string or simple value
      if (
        data.registration !== undefined &&
        data.registration !== null &&
        data.registration !== ""
      ) {
        const registrations = this.normalizeProposalPrefillRegistrations(
          data.registration,
        );
        if (registrations.length > 0) {
          updates.registrations = registrations;
        }
      }

      // Handle client name — split into first/last and add as joint client
      if (data.clientName) {
        const names = data.clientName.split("&").map((n) => n.trim());
        const jointClients = names.map((name, idx) => {
          const parts = name.split(" ");
          return {
            firstName: parts[0] || "",
            middleName: parts.length > 2 ? parts.slice(1, -1).join(" ") : "",
            lastName: parts.length > 1 ? parts[parts.length - 1] : "",
            clientName: name,
            isPrimary: idx === 0,
          };
        });
        updates.jointClients = jointClients;
      }
      if (
        isPcsRegistrationStrategy(
          data.strategy,
          this.props.lists?.proposalTypes || [],
        ) &&
        (updates.registrations || []).length > 1
      ) {
        updates.pcsRegistrationType = "Multi";
        updates.registrations = normalizePcsRegistrationRows(
          updates.registrations,
        ).map((registration, index) => ({
          ...registration,
          accountOwner:
            registration.accountOwner ||
            updates.jointClients?.[index]?.clientName ||
            "",
        }));
      } else if (
        isStandardRegistrationStrategy(
          data.strategy,
          this.props.lists?.proposalTypes || [],
        ) &&
        (updates.registrations || []).length > 1
      ) {
        updates.standardRegistrationType = "Multi";
        updates.registrations = normalizeStandardRegistrationRows(
          updates.registrations,
          createStandardRegistrationFields(updates),
        ).map((registration, index) => ({
          ...registration,
          accountOwner:
            registration.accountOwner ||
            updates.jointClients?.[index]?.clientName ||
            "",
        }));
      }

      // Show info banner for fusion proposals
      if (data.isFusion) {
        updates.proposalPrefillInfo =
          "Pre-filled from a Fusion proposal. Client details may need to be adjusted since Fusion proposals contain multiple strategies.";
      }

      // Preserve proposal's DateTime for merging onto the same row
      if (data.dateTime) {
        updates.proposalDateTime = new Date(data.dateTime).toUTCString();
      }

      if (Object.keys(updates).length > 0) {
        const registration = updates.registrations?.[0]?.value || "";
        const rules = getRegistrationRules(
          registration,
          updates.custodian || "",
        );
        const prefilledPcsMulti =
          updates.pcsRegistrationType === "Multi" &&
          isPcsRegistrationStrategy(
            data.strategy,
            this.props.lists?.proposalTypes || [],
          );
        const prefilledStandardMulti =
          updates.standardRegistrationType === "Multi" &&
          isStandardRegistrationStrategy(
            data.strategy,
            this.props.lists?.proposalTypes || [],
          );
        if (
          !prefilledPcsMulti &&
          !prefilledStandardMulti &&
          !rules.allowsSecondAccountHolder &&
          Array.isArray(updates.jointClients)
        ) {
          const primary =
            updates.jointClients.find((client) => client?.isPrimary) ||
            updates.jointClients[0];
          updates.jointClients = primary
            ? [{ ...primary, isPrimary: true }]
            : [];
        }
        if (
          registration === "Custodial Roth IRA" &&
          !rules.isAvailableForCustodian
        ) {
          updates.registrations = this.getSingleRegistrationState().map(
            (current) => ({ ...current, value: "" }),
          );
        }
        this.setState(updates);
      }
    } catch (e) {
      console.log("Error loading proposal pre-fill data:", e);
    }
  };

  loadRiskChart = async () => {
    try {
      const result = await getS3File("vicus-proposals", "Templates/ISP/risk-q5-chart.png");
      if (result?.data && this._mounted) {
        this.setState({ riskChartUrl: `data:image/png;base64,${result.data}` });
      }
    } catch (e) {
      console.log("Could not load risk chart:", e.message);
    }
  };

  componentWillUnmount() {
    this._mounted = false;
    this._customFeesRequestId += 1;
    document.removeEventListener("keydown", this.handleGlobalKeyDown);
  }

  handleGlobalKeyDown = (e) => {
    if (e.key === "Escape") {
      if (this.state.showZeroFeeModal) {
        this.handleCancelZeroFee();
      }
      // Don't close success modal on Escape — user should choose an action
    }
  };

  componentDidUpdate(prevProps, prevState) {
    // Auto-set default advisor when lists load
    const defaultAdvisor = this.props.lists?.userInfo?.DefaultAdvisor;
    const prevDefaultAdvisor = prevProps.lists?.userInfo?.DefaultAdvisor;
    if (defaultAdvisor && defaultAdvisor !== prevDefaultAdvisor && !this.state.advisorName) {
      this.setState({ advisorName: defaultAdvisor });
    }

    const evolutionTiers = this.props.lists?.userInfo?.EvolutionTiers;
    const prevTiers = prevProps.lists?.userInfo?.EvolutionTiers;
    if (this.state.strategy !== prevState.strategy) {
      this.fetchManagementFeeTierOptions();
    } else if (evolutionTiers !== prevTiers) {
      // An EvolutionTiers change re-derives the available tier options. For
      // Fusion the tier list IS the Evolution tiers, so re-seed from them.
      const rawTiers = this.isFusionStrategy()
        ? ["Standard", ...splitEvolutionTiers(evolutionTiers)]
        : this.state.availableManagementFeeTiers;
      this.syncManagementFeeTierOptions(rawTiers);
    }

    // Fetch custom fees when advisor or strategy changes (covers mount, prefill, and direct setState)
    if (this.state.advisorName !== prevState.advisorName || this.state.strategy !== prevState.strategy) {
      this.fetchCustomFees();
    }

    // If the advisor changed and the currently selected strategy is now
    // advisor-scoped away, clear it so the description/validation don't cling.
    const advisorChanged =
      this.state.advisorName !== prevState.advisorName ||
      this.state.additionalAdvisors !== prevState.additionalAdvisors;
    if (
      advisorChanged &&
      this.state.strategy &&
      !this.isExistingFeeUpdate()
    ) {
      const proposalTypes = this.props.lists?.proposalTypes || [];
      const pt = proposalTypes.find((p) => p.name === this.state.strategy);
      const candidates = [
        this.state.advisorName,
        ...(this.state.additionalAdvisors || []),
      ];
      if (pt && !isStrategyAvailableForAdvisors(pt, candidates)) {
        this.setState({ strategy: "" });
      }
    }

    // Enforce the single-available-bond-type lock for paths that set
    // modelSelection directly (edit-restore, proposal prefill) and so bypass
    // handleFieldChange. The !== guard makes this idempotent and loop-safe.
    if (
      this.state.modelSelection !== prevState.modelSelection ||
      this.state.strategy !== prevState.strategy
    ) {
      const available = this.getAvailableBondTypesForModel();
      if (
        !this.isExistingRiskToleranceUpdate() &&
        available.length === 1 &&
        this.state.bondType !== available[0]
      ) {
        this.setState({ bondType: available[0] });
      }
    }

    // Fetch management fees when strategy or the resolved management fee tier changes
    if (
      this.state.strategy !== prevState.strategy ||
      this.state.selectedManagementFeeTierKey !== prevState.selectedManagementFeeTierKey
    ) {
      this.fetchManagementFees();
    }

    const currentFeeUpdateStrategies = this.getFeeUpdateStrategies().join("|");
    const previousFeeUpdateStrategies = this.getFeeUpdateStrategies(
      prevState.feeUpdateAccounts,
      prevState.requestType,
      prevState.existingRequestAction,
    ).join("|");
    if (
      currentFeeUpdateStrategies !== previousFeeUpdateStrategies ||
      this.state.selectedManagementFeeTierKey !==
        prevState.selectedManagementFeeTierKey
    ) {
      this.fetchFeeUpdateStrategyFees();
    }
  }

  getProposalPrefillHistory = () => {
    const proposals = Array.isArray(this.props.lists?.proposals)
      ? this.props.lists.proposals
      : [];
    const resetAt = Date.parse(this.state.prefillResetAt || "");
    if (Number.isNaN(resetAt)) return proposals;

    return proposals.filter((proposal) => {
      const proposalDate = Date.parse(
        proposal?.DateTime ||
          proposal?.["date-time"] ||
          proposal?.localDateTime ||
          "",
      );
      return !Number.isNaN(proposalDate) && proposalDate > resetAt;
    });
  };

  // Build client name history from previous submissions for autocomplete.
  // A user reset excludes older submissions but allows new submissions to
  // repopulate the list naturally.
  getClientNameHistory = () => {
    const names = [];
    this.getProposalPrefillHistory().forEach((p) => {
      if (p["CLIENT NAMES"] && !names.includes(p["CLIENT NAMES"])) {
        names.push(p["CLIENT NAMES"]);
      }
    });
    return names;
  };

  getFundingAmountHistory = () => {
    const amounts = [];
    this.getProposalPrefillHistory().forEach((proposal) => {
      const amount = String(
        proposal?.VALUE || proposal?.initialInvestmentAmount || "",
      ).trim();
      if (amount && !amounts.includes(amount)) amounts.push(amount);
    });
    return amounts;
  };

  handleResetPrefillSuggestions = () => {
    const reset = {
      resetAt: new Date().toISOString(),
      generation: this.state.prefillGeneration + 1,
    };
    try {
      localStorage.setItem(
        ISP_PREFILL_RESET_STORAGE_KEY,
        JSON.stringify(reset),
      );
    } catch (error) {
      // The in-memory reset still works when browser storage is unavailable.
    }
    this.setState({
      prefillResetAt: reset.resetAt,
      prefillGeneration: reset.generation,
      prefillResetNotice: true,
    });
  };

  // --- Section toggle ---
  toggleSection = (section) => {
    this.setState((prev) => {
      const updates = {
        expandedSection: prev.expandedSection === section ? "" : section,
      };
      if (section === "4") {
        updates.feesSectionVisited = true;
      }
      return updates;
    });
  };

  getFeeScheduleLabels = () => {
    const schedules = getIarFeeSchedules(this.state.customFees);
    return schedules[this.state.iarFeeScheduleType] || [];
  };

  findZeroFees = () => {
    const labels = this.getFeeScheduleLabels();
    const zeros = [];
    const isExplicitZero = (value) => {
      if (value === undefined || value === null) return false;
      const raw = String(value).trim();
      if (raw === "") return false;
      const numeric = Number(raw);
      return Number.isFinite(numeric) && numeric === 0;
    };
    const collect = (type, flat, tiered) => {
      if (type === "Fixed Percentage") {
        if (isExplicitZero(flat)) {
          zeros.push("Fixed Percentage Rate");
        }
        return;
      }
      if (type === "Tiered") {
        const tieredRates = normalizeIarFeeTiered(tiered, labels);
        const labelOccurrences = {};
        tieredRates.forEach((tier) => {
          const occurrence = (labelOccurrences[tier.label] = (labelOccurrences[tier.label] || 0) + 1);
          const val = tier.rate;
          if (isExplicitZero(val)) {
            zeros.push(`${tier.label}${occurrence > 1 ? ` (#${occurrence})` : ""}`);
          }
        });
      }
    };
    if (this.hasPath()) {
      collect(
        this.state.iarFeeType || "Fixed Percentage",
        this.state.iarFeeFlat,
        this.state.iarFeeTiered,
      );
      collect(this.state.pathIarFeeType, this.state.pathIarFeeFlat, this.state.pathIarFeeTiered);
    } else {
      collect(this.getEffectiveIarFeeType(), this.state.iarFeeFlat, this.state.iarFeeTiered);
    }
    return zeros;
  };

  handleConfirmZeroFee = () => {
    if (this._zeroFeeConfirmationInProgress || this.state.isSubmitting) return;

    const zeroFields = this.findZeroFees();
    if (zeroFields.length === 0) {
      this.setState({
        showZeroFeeModal: false,
        zeroFeeConfirmationPending: false,
      });
      return;
    }

    this._zeroFeeConfirmationInProgress = true;
    const userEmail = this.props.user || "unknown";
    const waiver = {
      confirmedAt: new Date().toLocaleString(),
      confirmedBy: userEmail,
      zeroFields,
    };
    this.setState(
      {
        iarFeeWaiver: waiver,
        showZeroFeeModal: false,
        feesSectionVisited: true,
        zeroFeeConfirmationPending: true,
      },
      this.handleSubmit,
    );
  };

  handleCancelZeroFee = () => {
    this._zeroFeeConfirmationInProgress = false;
    this.setState({
      showZeroFeeModal: false,
      zeroFeeConfirmationPending: false,
    });
  };

  // --- Generic field handler ---
  isReducedExistingAccountAction = (action) =>
    getRequestCapabilities("Existing", action).isReducedUpdate;

  getSingleRegistrationState = () => {
    const first = this.state.registrations?.[0] || {
      registration: "registration0",
      value: "",
      percentage: "100",
    };
    return [
      {
        ...first,
        registration: "registration0",
        accountOwner: "",
        percentage: first.percentage || "100",
      },
    ];
  };

  getReducedAccountInfoReset = (action) => ({
    custodian: "",
    bondType: action === "Risk Tolerance Update" ? "" : "Taxable",
    bondTypePreferenceProvided: false,
    modelSelection: "",
    pathBondType: "",
    initialInvestmentAmount: "",
    accountNetWorthPct: "",
    accountIncomeNeed: "",
    accountIncomeAmount: "",
    accountIncomeStartDate: "",
    accountIncomeEndDate: "",
    accountIncomeFrequency: "",
    accountGoal: "",
    accountGoalOther: "",
    sourceOfFunds: [],
    sourceOfFundsText: {},
  });

  getRiskToleranceReset = () => ({
    capacityAnswers: {},
    willingnessAnswers: {},
    capacityTotal: 0,
    willingnessTotal: 0,
    combinedScore: 0,
    riskCategory: "",
    selectedPortfolio: "",
    portfolioOverride: false,
    overrideReason: "",
    overrideOtherText: "",
  });

  handleFieldChange = (field, value) => {
    if (field === "iarFeeTier" || field === "strategy") {
      this._restoredManagementFeeTierSelection = "";
    }
    const updates = field === "iarFeeTier"
      ? (() => {
        const resolvedSelection = resolveManagementFeeTierSelection(
          value,
          this.state.availableManagementFeeTierOptions
        );
        return {
          iarFeeTier: resolvedSelection.label,
          selectedManagementFeeTierKey: resolvedSelection.exactTier,
        };
      })()
      : { [field]: value };
    if (field === "custodianFullName") {
      updates.custodialAdultName = value;
    }
    if (field === "advisorName" || field === "strategy") {
      Object.assign(updates, {
        customFees: null,
        customFeesLoading: !!(field === "advisorName"
          ? value
          : this.state.advisorName),
        customFeesError: "",
        iarFeeScheduleType: "",
        iarFeeTiered: [],
        pathIarFeeTiered: [],
      });
    }
    if (field === "iarFeeScheduleType") {
      const resolved = resolveIarFeeSchedule(this.state.customFees, value);
      updates.iarFeeTiered = reconcileTieredRatesForSchedule(
        [],
        resolved.labels,
        resolved.rows,
      );
      updates.pathIarFeeTiered = reconcileTieredRatesForSchedule(
        [],
        resolved.labels,
        resolved.rows,
      );
    }
    if (field === "iarFeeType") {
      if (value === "Fixed Percentage") updates.iarFeeTiered = [];
      if (value === "Tiered") updates.iarFeeFlat = "";
    }
    if (field === "pathIarFeeType") {
      if (value === "Fixed Percentage") updates.pathIarFeeTiered = [];
      if (value === "Tiered") updates.pathIarFeeFlat = "";
    }
    if (field === "bondType") {
      updates.bondTypePreferenceProvided =
        this.isExistingRiskToleranceUpdate() &&
        this.isBondTypePromptShown() &&
        ["Taxable", "Municipal"].includes(value);
    }
    if (field === "custodian") {
      const registration = this.state.registrations?.[0]?.value || "";
      if (
        registration === "Custodial Roth IRA" &&
        !getRegistrationRules(registration, value, {
          requestType: this.state.requestType,
          existingRequestAction: this.state.existingRequestAction,
        }).isAvailableForCustodian
      ) {
        updates.registrations = this.getSingleRegistrationState().map(
          (current) => ({ ...current, value: "" }),
        );
        updates.custodialAdultName = "";
        updates.custodianFullName = "";
        updates.relationshipToPrimary = "";
      }
    }
    // A Risk Tolerance Update preference does not change the available
    // portfolios, so selecting or clearing it must not erase the RTQ answer.
    if (
      field === "strategy" ||
      (field === "bondType" && !this.isExistingRiskToleranceUpdate())
    ) {
      updates.selectedPortfolio = "";
    }
    if (field === "requestType") {
      updates.accountNumber = "";
      updates.accountNumbers = [""];
      updates.feeUpdateAccounts = [createEmptyFeeUpdateAccount()];
      updates.pcsRegistrationType = "Single";
      updates.standardRegistrationType = "Single";
      updates.bondTypePreferenceProvided = false;
      if (value !== "Existing") {
        updates.existingRequestAction = "";
        if (this.isExistingRiskToleranceUpdate()) {
          updates.bondType = "Taxable";
        }
        const currentRegistration =
          this.state.registrations?.[0]?.value || "";
        const registrationPermitted =
          value !== "New" ||
          getRegistrationRules(
            currentRegistration,
            this.state.custodian,
          ).isAvailableForCustodian;
        if (!registrationPermitted) {
          updates.registrations = this.getSingleRegistrationState().map(
            (registration) => ({ ...registration, value: "" }),
          );
        }
        Object.assign(
          updates,
          getConditionalStateReset({
            requestType: value,
            existingRequestAction: "",
            registration: registrationPermitted ? currentRegistration : "",
            custodian: this.state.custodian,
            jointClients: this.state.jointClients,
            registrationTypes: this.props.lists?.registrationTypes || [],
          }),
        );
      }
    }
    if (field === "requestType" && value !== "New") {
      updates.clientProfileImaSubmitted = "";
      updates.householding = "";
      updates.householdRefName = "";
      updates.householdRefLast4 = "";
      updates.householdRef = "";
      updates.householdReason = "";
      updates.householdReasonType = "";
      updates.householdReasonOther = "";
    }
    if (field === "existingRequestAction") {
      updates.accountNumber = "";
      updates.accountNumbers = [""];
      updates.feeUpdateAccounts = [createEmptyFeeUpdateAccount()];
      updates.pcsRegistrationType = "Single";
      updates.standardRegistrationType = "Single";
      if (!isStrategyChangeWorkflowAction(value)) {
        updates.clientProfileImaSubmitted = "";
      }
      if (value !== "Fee Update") {
        updates.registrations = this.getSingleRegistrationState();
      } else {
        updates.strategy = "";
        updates.registrations = [
          { registration: "registration0", value: "", percentage: "100" },
        ];
      }
      const registrationForTarget =
        value === "Fee Update"
          ? ""
          : this.state.registrations?.[0]?.value || "";
      const conditionalReset = getConditionalStateReset({
        requestType: "Existing",
        existingRequestAction: value,
        registration: registrationForTarget,
        custodian: this.state.custodian,
        jointClients: this.state.jointClients,
        registrationTypes: this.props.lists?.registrationTypes || [],
      });
      if (this.isReducedExistingAccountAction(value)) {
        Object.assign(
          updates,
          this.getReducedAccountInfoReset(value),
          conditionalReset,
        );
      } else {
        Object.assign(updates, conditionalReset);
        updates.bondType = "Taxable";
        updates.bondTypePreferenceProvided = false;
      }
      if (value === "Fee Update" || value === "Model Change") {
        Object.assign(updates, this.getRiskToleranceReset());
      }
    }
    // Clear model selection and recalculate risk scores when strategy changes
    if (field === "strategy") {
      const nextIsManagedMoneyMarket = this.isManagedMoneyMarketStrategy(value);
      const managedMoneyMarketModeChanged =
        this.isManagedMoneyMarketStrategy(this.state.strategy) !== nextIsManagedMoneyMarket;
      updates.modelSelection = "";
      if (nextIsManagedMoneyMarket) {
        updates.bondType = "";
        updates.portfolioOverride = false;
        updates.overrideReason = "";
        updates.overrideOtherText = "";
      }
      updates.iarFeeTier = "";
      updates.selectedManagementFeeTierKey = "";
      updates.availableManagementFeeTiers = [];
      updates.availableManagementFeeTierOptions = [];
      updates.fusionAllocations = [];
      updates.fusionSubFees = {};
      updates.fusionSubFeesLoading = false;
      updates.fusionFeeValidation = { isValid: true, issues: [] };
      if (this.isExistingRiskToleranceUpdate()) {
        updates.bondType = "";
        updates.bondTypePreferenceProvided = false;
      }
      updates.fusionRegistrationType = String(value || "")
        .toLowerCase()
        .includes("fusion")
        ? "Single"
        : "";
      updates.fusionRebalanceType = String(value || "")
        .toLowerCase()
        .includes("fusion")
        ? "Automatic"
        : "";
      updates.pcsRegistrationType = "Single";
      updates.standardRegistrationType = "Single";
      updates.registrations = this.getSingleRegistrationState();
      if (managedMoneyMarketModeChanged) {
        updates.accountGoal = nextIsManagedMoneyMarket ? [] : "";
        updates.accountGoalOther = "";
      }
      Object.assign(updates, computeRiskScores(this.state.capacityAnswers, this.state.willingnessAnswers));
      // Recompute the path bond default for the newly selected strategy
      // (state.strategy isn't updated yet, so inspect `value`).
      const pathPt = this.props.lists?.proposalTypes?.find((p) => p.name === value);
      const pathName = this.shouldSuppressPathStrategy(pathPt) ? "" : pathPt?.pathStrategy || "";
      const pathTypes = pathName ? this.getBondTypesForStrategyName(pathName) : [];
      const advHasChoice = pathTypes.includes("Taxable") && pathTypes.includes("Municipal");
      updates.pathBondType = advHasChoice ? "Taxable" : "";
    }
    if (field === "fusionRegistrationType") {
      if (value === "Multi") {
        updates.fusionRebalanceType = "Manual";
        this.restoreDeferredJointClients(updates);
      } else {
        if (!this.state.fusionRebalanceType) {
          updates.fusionRebalanceType = "Automatic";
        }
        updates.fusionAllocations = this.normalizeFusionAllocations(
          this.state.fusionAllocations,
          "Single",
        );
      }
    }
    if (field === "pcsRegistrationType") {
      if (value === "Multi") {
        this.restoreDeferredJointClients(updates);
        if (!this.isExistingFeeUpdate()) {
          updates.registrations = normalizePcsRegistrationRows(
            this.state.registrations,
          );
        }
      } else if (this.isExistingFeeUpdate()) {
        const firstAccount = this.state.feeUpdateAccounts?.[0] ||
          createEmptyFeeUpdateAccount();
        const feeUpdateAccounts = [{ ...firstAccount, accountOwner: "" }];
        const legacyFields = buildFeeUpdateLegacyFields(feeUpdateAccounts);
        updates.feeUpdateAccounts = feeUpdateAccounts;
        updates.accountNumbers = legacyFields.accountNumbers;
        updates.registrations = legacyFields.registrations;
        updates.accountNumber = legacyFields.accountNumbers
          .filter(Boolean)
          .join(", ");
      } else {
        updates.registrations = this.getSingleRegistrationState();
      }
    }
    if (field === "standardRegistrationType") {
      if (value === "Multi") {
        this.restoreDeferredJointClients(updates);
        if (this.isExistingFeeUpdate()) {
          updates.feeUpdateAccounts = this.state.feeUpdateAccounts.map(
            (account) => ({
              ...account,
              ...createStandardRegistrationFields(account),
            }),
          );
        } else {
          updates.registrations = normalizeStandardRegistrationRows(
            this.state.registrations,
            createStandardRegistrationFields(this.state),
          );
        }
      } else if (this.isExistingFeeUpdate()) {
        const firstAccount = this.state.feeUpdateAccounts?.[0] ||
          createEmptyFeeUpdateAccount();
        const feeUpdateAccounts = [firstAccount];
        const legacyFields = buildFeeUpdateLegacyFields(feeUpdateAccounts);
        updates.feeUpdateAccounts = feeUpdateAccounts;
        updates.accountNumbers = legacyFields.accountNumbers;
        updates.registrations = legacyFields.registrations;
        updates.accountNumber = legacyFields.accountNumbers
          .filter(Boolean)
          .join(", ");
      } else {
        const first = this.state.registrations?.[0] || {};
        updates.registrations = [
          {
            registration: "registration0",
            value: first.value || "",
            percentage: "100",
          },
        ];
        Object.assign(updates, createStandardRegistrationFields(first));
      }
    }
    // Lock the bond type when the chosen program only exists under one bond
    // type (e.g. Government Money Market is taxable only). Clearing the
    // portfolio mirrors the bondType-change branch above since the narrowed
    // risk-tolerance options change with it.
    if (field === "modelSelection") {
      if (this.isManagedMoneyMarketStrategy()) {
        const selection = canonicalizeMmmsFundSelection(value);
        updates.modelSelection = selection;
        updates.selectedPortfolio = selection;
        updates.bondType = getMmmsFundBondType(selection);
        updates.portfolioOverride = false;
        updates.overrideReason = "";
        updates.overrideOtherText = "";
      } else {
        const available = this.getAvailableBondTypesForModel(value);
        if (available.length === 1 && this.state.bondType !== available[0]) {
          updates.bondType = available[0];
          updates.selectedPortfolio = "";
        }
        // Even when the bond type isn't locked, a new program narrows the
        // risk-tolerance list. Clear a now-orphaned selection so the Section 3
        // dropdown doesn't retain a value that the new program does not offer.
        if (updates.selectedPortfolio !== "" && this.state.selectedPortfolio) {
          const nextBondType = updates.bondType || this.state.bondType;
          const options = this.getRiskToleranceOptions({
            modelSelection: value,
            bondType: nextBondType,
          });
          if (options.length > 0 && !options.includes(this.state.selectedPortfolio)) {
            updates.selectedPortfolio = "";
          }
        }
      }
    }
    // Any fee or fee-context change requires a fresh final-submit acknowledgment.
    if ([
      "advisorName",
      "strategy",
      "iarFeeTier",
      "selectedManagementFeeTierKey",
      "iarFeeType",
      "iarFeeFlat",
      "iarFeeTiered",
      "iarFeeScheduleType",
      "pathIarFeeType",
      "pathIarFeeFlat",
      "pathIarFeeTiered",
    ].includes(field)) {
      updates.iarFeeWaiver = null;
    }
    this.setState(updates);
  };

  fetchCustomFees = async () => {
    const { advisorName, strategy } = this.state;
    const requestId = ++this._customFeesRequestId;
    if (!advisorName) {
      if (this._mounted) {
        this.setState({
          customFees: null,
          customFeesLoading: false,
          customFeesError: "",
          iarFeeScheduleType: "",
          iarFeeTiered: [],
          pathIarFeeTiered: [],
        });
      }
      return;
    }

    this.setState({
      customFees: null,
      customFeesLoading: true,
      customFeesError: "",
    });

    try {
      const result = await getCustomFees(advisorName, strategy || "");
      const fees = result?.body ? JSON.parse(result.body) : result;
      if (
        !this._mounted ||
        requestId !== this._customFeesRequestId ||
        this.state.advisorName !== advisorName ||
        this.state.strategy !== strategy
      ) {
        return;
      }

      if (!fees || typeof fees !== "object" || fees.errorMessage) {
        throw new Error("Invalid custom-fee response");
      }
      if (!isCustomFeeResponseAuthorizedForAdvisor(fees, advisorName)) {
        throw new Error("Custom-fee response belongs to a different advisor");
      }

      this.setState((prevState) => {
        if (
          requestId !== this._customFeesRequestId ||
          prevState.advisorName !== advisorName ||
          prevState.strategy !== strategy
        ) {
          return null;
        }

        const resolved = resolveIarFeeSchedule(
          fees,
          prevState.iarFeeScheduleType,
        );
        return {
          customFees: fees,
          customFeesLoading: false,
          customFeesError: "",
          iarFeeScheduleType: resolved.selectedName,
          iarFeeTiered: reconcileTieredRatesForSchedule(
            prevState.iarFeeTiered,
            resolved.labels,
            resolved.rows,
          ),
          pathIarFeeTiered: reconcileTieredRatesForSchedule(
            prevState.pathIarFeeTiered,
            resolved.labels,
            resolved.rows,
          ),
        };
      });
    } catch (e) {
      console.log("Failed to load custom fees:", e.message);
      if (
        this._mounted &&
        requestId === this._customFeesRequestId &&
        this.state.advisorName === advisorName &&
        this.state.strategy === strategy
      ) {
        this.setState({
          customFees: {},
          customFeesLoading: false,
          customFeesError:
            "Unable to verify an IAR fee schedule for this advisor and strategy.",
          iarFeeScheduleType: "",
          iarFeeTiered: [],
          pathIarFeeTiered: [],
        });
      }
    }
  };

  getSelectedManagementFeeTierKey = (
    selection = this.state.selectedManagementFeeTierKey || this.state.iarFeeTier
  ) => {
    const resolvedSelection = resolveManagementFeeTierSelection(
      selection,
      this.state.availableManagementFeeTierOptions
    );
    return resolvedSelection.exactTier || this.state.selectedManagementFeeTierKey || "";
  };

  getManagementFeeTierCandidates = (
    selection = this.state.selectedManagementFeeTierKey || this.state.iarFeeTier
  ) => {
    const collapsedLabel = collapseTierLabel(selection);
    if (!collapsedLabel) return [];

    const strategyExactTiers = (this.state.availableManagementFeeTiers || [])
      .map((tier) => normalizeTierKey(tier))
      .filter(Boolean);
    const userExactTiers = splitEvolutionTiers(this.props.lists?.userInfo?.EvolutionTiers)
      .map((tier) => normalizeTierKey(tier))
      .filter(Boolean);
    const strategyTierSet = new Set(strategyExactTiers);
    const candidates = [];

    const pushCandidate = (tier) => {
      if (!tier || candidates.includes(tier)) return;
      candidates.push(tier);
    };

    const selectedTier = this.getSelectedManagementFeeTierKey(selection);
    pushCandidate(selectedTier);

    userExactTiers.forEach((tier) => {
      if (collapseTierLabel(tier) !== collapsedLabel) return;
      if (strategyExactTiers.length > 0 && !strategyTierSet.has(tier)) return;
      pushCandidate(tier);
    });

    strategyExactTiers.forEach((tier) => {
      if (collapseTierLabel(tier) === collapsedLabel) {
        pushCandidate(tier);
      }
    });

    return candidates;
  };

  loadManagementFeesForTierCandidates = async (strategy, tierCandidates = []) => {
    // Try every candidate in priority order, then fall back to the
    // collapsed label (e.g., "Tier B") if it isn't already in the list.
    // Sub-strategies often have management-fee rows keyed by the IM tier
    // alone, even when the user's selected tier carries an IAR-schedule
    // suffix (e.g., "Tier B WK"). Without this fallback, Fusion sleeves
    // whose available tiers differ from the parent strategy's surface
    // "no tier schedule found" even though the data exists.
    const seen = new Set();
    const tryList = [];
    const push = (t) => {
      if (!t || seen.has(t)) return;
      seen.add(t);
      tryList.push(t);
    };
    tierCandidates.forEach(push);
    tierCandidates.forEach((t) => push(collapseTierLabel(t)));

    for (const tier of tryList) {
      try {
        const result = await getManagementFees(strategy, tier);
        const fees = result?.body ? JSON.parse(result.body) : result;
        if (fees && typeof fees === "object" && !fees.errorMessage && Object.keys(fees).length > 0) {
          return {
            tier,
            fees: sortManagementFeeTiers(fees),
          };
        }
      } catch (e) {
        console.log(`Failed to load management fees for ${strategy} at ${tier}:`, e.message);
      }
    }

    return {
      tier: tierCandidates[0] || "",
      fees: null,
    };
  };

  syncManagementFeeTierOptions = (
    rawTiers = this.state.availableManagementFeeTiers,
    preferredSelection = this.state.selectedManagementFeeTierKey || this.state.iarFeeTier
  ) => {
    const availableManagementFeeTierOptions = buildManagementFeeTierOptions(
      rawTiers,
      this.props.lists?.userInfo?.EvolutionTiers,
      this._restoredManagementFeeTierSelection,
    );

    let nextTier = "";
    let nextTierKey = "";

    const resolvedSelection = resolveManagementFeeTierSelection(
      preferredSelection,
      availableManagementFeeTierOptions
    );

    const standardOption = availableManagementFeeTierOptions.find(
      (option) => option.label === "Standard"
    );

    if (resolvedSelection.exactTier) {
      nextTier = resolvedSelection.label;
      nextTierKey = resolvedSelection.exactTier;
    } else if (standardOption) {
      nextTier = standardOption.label;
      nextTierKey = standardOption.exactTier;
    } else if (availableManagementFeeTierOptions.length === 1) {
      nextTier = availableManagementFeeTierOptions[0].label;
      nextTierKey = availableManagementFeeTierOptions[0].exactTier;
    }

    this.setState((prev) => {
      const rawTiersChanged =
        JSON.stringify(prev.availableManagementFeeTiers) !== JSON.stringify(rawTiers);
      const optionsChanged =
        JSON.stringify(prev.availableManagementFeeTierOptions) !== JSON.stringify(availableManagementFeeTierOptions);

      if (
        !rawTiersChanged &&
        !optionsChanged &&
        prev.iarFeeTier === nextTier &&
        prev.selectedManagementFeeTierKey === nextTierKey
      ) {
        return null;
      }

      return {
        availableManagementFeeTiers: rawTiers,
        availableManagementFeeTierOptions,
        iarFeeTier: nextTier,
        selectedManagementFeeTierKey: nextTierKey,
      };
    });
  };

  fetchManagementFeeTierOptions = async () => {
    const { strategy } = this.state;
    const fallbackTiers = splitEvolutionTiers(this.props.lists?.userInfo?.EvolutionTiers);
    if (!strategy) {
      this.setState({
        availableManagementFeeTiers: [],
        availableManagementFeeTierOptions: [],
        iarFeeTier: "",
        selectedManagementFeeTierKey: "",
      });
      return;
    }

    // Fusion has no parent-level tier table — each sub-strategy carries its
    // own schedule. The tier choice is dictated purely by the user's
    // Evolution tiers, run through the same buildManagementFeeTierOptions
    // collapsing logic as every other strategy. The "Standard" baseline keeps
    // the "no Evolution tiers" / "standard-only" cases collapsing to a single
    // Standard option (subsection hidden), matching non-Fusion behavior.
    if (this.isFusionStrategy()) {
      this.syncManagementFeeTierOptions(["Standard", ...fallbackTiers]);
      return;
    }

    try {
      const result = await getManagementFeeTiers(strategy);
      const tierResponse = result?.body ? JSON.parse(result.body) : result;
      const rawTiers = Array.isArray(tierResponse?.tiers) && tierResponse.tiers.length > 0
        ? tierResponse.tiers
        : fallbackTiers;
      if (!this._mounted) return;
      this.syncManagementFeeTierOptions(rawTiers);
    } catch (e) {
      console.log("Failed to load management fee tiers:", e.message);
      if (!this._mounted) return;
      this.syncManagementFeeTierOptions(fallbackTiers);
    }
  };

  isFusionStrategy = () => this.state.strategy?.toLowerCase().includes("fusion") || false;

  isPcsRegistrationTypeEligibleForState = (state = this.state) => {
    if (
      !supportsPcsRegistrationType(
        state.requestType,
        state.existingRequestAction,
      )
    ) {
      return false;
    }
    const proposalTypes = this.props.lists?.proposalTypes || [];
    const isFeeUpdate =
      state.requestType === "Existing" &&
      state.existingRequestAction === "Fee Update";
    if (!isFeeUpdate) {
      return isPcsRegistrationStrategy(state.strategy, proposalTypes);
    }
    const strategies = (state.feeUpdateAccounts || [])
      .map((account) => String(account?.strategy || "").trim())
      .filter(Boolean);
    return strategies.length > 0
      ? strategies.every((strategy) =>
          isPcsRegistrationStrategy(strategy, proposalTypes),
        )
      : isPcsRegistrationStrategy(state.strategy, proposalTypes);
  };

  isPcsMultiRegistration = (state = this.state) =>
    this.isPcsRegistrationTypeEligibleForState(state) &&
    state.pcsRegistrationType === "Multi";

  isStandardRegistrationTypeEligibleForState = (state = this.state) => {
    if (
      !supportsStandardRegistrationType(
        state.requestType,
        state.existingRequestAction,
      )
    ) {
      return false;
    }
    const proposalTypes = this.props.lists?.proposalTypes || [];
    const isFeeUpdate =
      state.requestType === "Existing" &&
      state.existingRequestAction === "Fee Update";
    if (!isFeeUpdate) {
      return isStandardRegistrationStrategy(state.strategy, proposalTypes);
    }
    const strategies = (state.feeUpdateAccounts || [])
      .map((account) => String(account?.strategy || "").trim())
      .filter(Boolean);
    return (
      strategies.length > 0 &&
      new Set(strategies).size === 1 &&
      strategies.every((strategy) =>
        isStandardRegistrationStrategy(strategy, proposalTypes),
      )
    );
  };

  isStandardMultiRegistration = (state = this.state) =>
    this.isStandardRegistrationTypeEligibleForState(state) &&
    state.standardRegistrationType === "Multi";

  hasInvalidPcsFeeUpdateRegistrationCount = (state = this.state) =>
    state.requestType === "Existing" &&
    state.existingRequestAction === "Fee Update" &&
    this.isPcsRegistrationTypeEligibleForState(state) &&
    state.pcsRegistrationType === "Single" &&
    (state.feeUpdateAccounts || []).length !== 1;

  toTierSelectionValue = (tier) => {
    return collapseTierLabel(tier);
  };

  normalizeFusionAllocations = (
    fusionAllocations = [],
    fusionRegistrationType = this.state.fusionRegistrationType,
  ) => {
    if (!Array.isArray(fusionAllocations)) return [];
    const includeAllocationRegistration = fusionRegistrationType === "Multi";
    const proposalTypes = this.props.lists?.proposalTypes || [];
    return fusionAllocations.map((allocation) => {
      const normalizedAllocation = {
        strategy: allocation?.strategy || "",
        riskTolerance: allocation?.riskTolerance || "",
        taxSensitivity: isPrivateClientStrategyAlias(
          allocation?.strategy,
          proposalTypes,
        )
          ? ""
          : allocation?.taxSensitivity || "",
        registration: includeAllocationRegistration
          ? allocation?.registration || ""
          : "",
        accountOwner: includeAllocationRegistration
          ? allocation?.accountOwner || ""
          : "",
        percentage: allocation?.percentage ?? "",
      };
      const privateClientStrategy = isPrivateClientStrategyAlias(
        normalizedAllocation.strategy,
        proposalTypes,
      );
      normalizedAllocation.displayName =
        privateClientStrategy || !allocation?.displayName
          ? getFusionAllocationDisplayName(
              normalizedAllocation,
              proposalTypes,
            )
          : allocation.displayName;
      return normalizedAllocation;
    });
  };

  normalizeLegacyFusionFeeState = (
    iarFeeTier,
    fusionAllocations = [],
    fusionRegistrationType = this.state.fusionRegistrationType,
  ) => {
    const normalizedAllocations = this.normalizeFusionAllocations(
      fusionAllocations,
      fusionRegistrationType,
    );
    const legacyTier =
      fusionAllocations.find((allocation) => allocation?.tier)?.tier || "";
    const normalizedTier =
      iarFeeTier && String(iarFeeTier).toLowerCase() !== "blended"
        ? iarFeeTier
        : legacyTier || "";

    return {
      iarFeeTier: this.toTierSelectionValue(normalizedTier),
      selectedManagementFeeTierKey: normalizeTierKey(normalizedTier),
      fusionAllocations: normalizedAllocations,
    };
  };

  getFusionFeeKey = (strategy, tier = this.getSelectedManagementFeeTierKey()) => {
    return buildFusionFeeKey(strategy, tier);
  };

  normalizeTier = (tier) => {
    return normalizeTierKey(tier);
  };

  getFeeUpdateStrategies = (
    accounts = this.state.feeUpdateAccounts,
    requestType = this.state.requestType,
    existingRequestAction = this.state.existingRequestAction,
  ) => {
    if (
      requestType !== "Existing" ||
      existingRequestAction !== "Fee Update"
    ) {
      return [];
    }
    return Array.from(
      new Set(
        (accounts || [])
          .map((account) => String(account?.strategy || "").trim())
          .filter(Boolean),
      ),
    );
  };

  fetchFeeUpdateStrategyFees = async () => {
    const requestId = ++this._feeUpdateStrategyFeesRequestId;
    const strategies = this.getFeeUpdateStrategies();
    const tierCandidates = this.getManagementFeeTierCandidates();
    const shouldLoad =
      strategies.length > 1 &&
      tierCandidates.length > 0 &&
      !getFeeUpdateStrategyCompatibilityIssue(this.state.feeUpdateAccounts);

    if (!shouldLoad) {
      if (
        this.state.feeUpdateStrategyFeeBlocks.length > 0 ||
        this.state.feeUpdateStrategyFeesLoading ||
        this.state.feeUpdateStrategyFeesError
      ) {
        this.setState({
          feeUpdateStrategyFeeBlocks: [],
          feeUpdateStrategyFeesLoading: false,
          feeUpdateStrategyFeesError: "",
        });
      }
      return;
    }

    this.setState({
      feeUpdateStrategyFeeBlocks: [],
      feeUpdateStrategyFeesLoading: true,
      feeUpdateStrategyFeesError: "",
    });
    const results = await Promise.all(
      strategies.map(async (strategy) => ({
        strategy,
        ...(await this.loadManagementFeesForTierCandidates(
          strategy,
          tierCandidates,
        )),
      })),
    );
    if (
      !this._mounted ||
      requestId !== this._feeUpdateStrategyFeesRequestId
    ) {
      return;
    }

    const missing = results
      .filter(
        ({ fees }) =>
          !fees ||
          (fees.flatFee == null &&
            (!Array.isArray(fees.tieredRates) ||
              fees.tieredRates.length === 0)),
      )
      .map(({ strategy }) =>
        getProgramDisplayName(strategy, this.props.lists?.proposalTypes),
      );
    if (missing.length > 0) {
      this.setState({
        feeUpdateStrategyFeeBlocks: [],
        feeUpdateStrategyFeesLoading: false,
        feeUpdateStrategyFeesError:
          `A management fee schedule could not be loaded for: ${missing.join(
            ", ",
          )}. Please contact Operations before submitting.`,
      });
      return;
    }

    this.setState({
      feeUpdateStrategyFeeBlocks: results.map(({ strategy, tier, fees }) => ({
        strategy: getProgramDisplayName(
          strategy,
          this.props.lists?.proposalTypes,
        ),
        rawStrategy: strategy,
        tier: collapseTierLabel(tier),
        tieredRates: Array.isArray(fees.tieredRates)
          ? sortFeeTiers(fees.tieredRates)
          : [],
        flatFeeRate: fees.flatFee ?? "",
        minimumNotes:
          fees.minimumFee == null
            ? []
            : [`Minimum account fee: $${fees.minimumFee}`],
      })),
      feeUpdateStrategyFeesLoading: false,
      feeUpdateStrategyFeesError: "",
    });
  };

  fetchManagementFees = async () => {
    const { strategy } = this.state;
    const tierCandidates = this.getManagementFeeTierCandidates();
    // Bump the token up front so any standard fetch already in flight is
    // invalidated, even when this call routes to the Fusion path instead.
    const requestId = ++this._managementFeesRequestId;
    if (!strategy || tierCandidates.length === 0) {
      this.setState({
        managementFees: null,
        managementFeesLoading: false,
        fusionSubFees: {},
        fusionSubFeesLoading: false,
        fusionFeeValidation: { isValid: true, issues: [] },
      });
      return;
    }
    if (this.isFusionStrategy()) {
      this.setState({
        managementFees: null,
        managementFeesLoading: false,
        fusionFeeValidation: { isValid: true, issues: [] },
      });
      this.fetchFusionSubFees();
      return;
    }
    this.setState({ managementFeesLoading: true, managementFees: null });
    try {
      const { tier, fees } = await this.loadManagementFeesForTierCandidates(strategy, tierCandidates);
      if (!this._mounted || requestId !== this._managementFeesRequestId) return;
      if (fees) {
        // Flat-fee strategies that name an pathStrategy preview the
        // post-path tier schedule alongside the flat row. Inject the
        // path row's tieredRates into `fees` so the existing tier
        // table renders without touching the schedule component.
        const pathName = this.getPathStrategyName();
        if (pathName && fees.flatFee != null) {
          try {
            const advResult = await getManagementFees(pathName, tier);
            if (!this._mounted || requestId !== this._managementFeesRequestId) return;
            // getManagementFees returns the raw Lambda response shape
            // ({statusCode, body}) for live calls, or the plain object in tests.
            // Mirror the parse used by loadManagementFeesForTierCandidates.
            const pathFees = advResult?.body ? JSON.parse(advResult.body) : advResult;
            if (pathFees?.tieredRates?.length > 0) {
              fees.tieredRates = sortFeeTiers(pathFees.tieredRates);
              // Carry the path row's minimum so the schedule can render
              // its own phase-labeled note under the tier table.
              if (pathFees.minimumFee != null) {
                fees.pathMinimumFee = pathFees.minimumFee;
              }
              // Stable signal that the path mgmt-fees row was fetched —
              // used by hasPath() to gate path-stage IAR rendering/validation.
              fees.pathStrategyRow = true;
            }
          } catch (pathErr) {
            console.log("Failed to load path fees:", pathErr.message);
          }
        }
        this.setState({
          managementFees: fees,
          managementFeesLoading: false,
          selectedManagementFeeTierKey: tier,
        });
      } else {
        this.setState({ managementFeesLoading: false });
      }
    } catch (e) {
      if (!this._mounted || requestId !== this._managementFeesRequestId) return;
      console.log("Failed to load management fees:", e.message);
      this.setState({ managementFeesLoading: false });
    }
  };

  retryFeeSchedules = async () => {
    if (!this.state.strategy || !this.state.advisorName) return;
    await this.fetchCustomFees();
    this.fetchManagementFees();
  };

  loadSharedFusionFeesForTierCandidates = (strategy, tierCandidates) => {
    const requestKey = JSON.stringify([strategy, tierCandidates]);
    const existingRequest = this._fusionFeeRequests.get(requestKey);
    if (existingRequest) return existingRequest;

    let sharedRequest;
    sharedRequest = this.loadManagementFeesForTierCandidates(
      strategy,
      tierCandidates,
    ).finally(() => {
      if (this._fusionFeeRequests.get(requestKey) === sharedRequest) {
        this._fusionFeeRequests.delete(requestKey);
      }
    });
    this._fusionFeeRequests.set(requestKey, sharedRequest);
    return sharedRequest;
  };

  getCachedFusionFeeEntry = (feeTables, strategy, tierCandidates) => {
    for (const tier of tierCandidates) {
      const entry = resolveFusionSubFeeEntry(
        feeTables,
        strategy,
        tier,
        collapseTierLabel(tier),
      );
      if (entry && validateManagementFeeTable(entry.fees).isComplete) {
        return { ...entry, tier };
      }
    }
    return null;
  };

  fetchFusionSubFees = async () => {
    const { fusionAllocations } = this.state;
    const tierCandidates = this.getManagementFeeTierCandidates();
    // Bump up front so any earlier fetch in flight is invalidated, including
    // when this call exits early without dispatching new requests.
    const requestId = ++this._fusionSubFeesRequestId;
    // Fetch fees for the FULL eligible-Fusion-sub-strategy roster (not just
    // the user's selected allocations). Compliance requirement: the PDF
    // shows every eligible sub-strategy's tier schedule. The form display
    // only renders the selected ones — filtering happens in
    // buildFusionFeeBlocks (form variant). Sub-strategies the user hasn't
    // allocated to still need their fees cached so the payload carries
    // them.
    const eligible = getEligibleFusionStrategies(
      this.props.lists?.proposalTypes || [],
      { bondType: this.state.bondType, allowIspOnly: true }
    );
    if (tierCandidates.length === 0 || eligible.length === 0) {
      this.setState({
        fusionSubFees: {},
        fusionBlendedFees: null,
        fusionSubFeesLoading: false,
        fusionFeeValidation: { isValid: true, issues: [] },
      });
      return;
    }
    this.setState({
      fusionSubFeesLoading: true,
      fusionFeeValidation: { isValid: true, issues: [] },
    });
    // Selected sleeves load first. The remaining eligible schedules are still
    // cached for the compliance PDF, but serially: this Lambda intentionally
    // has low reserved concurrency, so the former Promise.all fan-out could
    // turn one form edit into hundreds of throttled requests.
    const selectedOrder = new Map();
    fusionAllocations.forEach((allocation, index) => {
      if (allocation?.strategy && !selectedOrder.has(allocation.strategy)) {
        selectedOrder.set(allocation.strategy, index);
      }
    });
    const orderedEligible = [...eligible].sort((left, right) => {
      const leftOrder = selectedOrder.has(left.name)
        ? selectedOrder.get(left.name)
        : Number.MAX_SAFE_INTEGER;
      const rightOrder = selectedOrder.has(right.name)
        ? selectedOrder.get(right.name)
        : Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder;
    });

    const priorResults = this.state.fusionSubFees || {};
    const results = {};
    let resolvedTier = "";
    for (const pt of orderedEligible) {
      if (!this._mounted || requestId !== this._fusionSubFeesRequestId) return;

      const cached = this.getCachedFusionFeeEntry(
        priorResults,
        pt.name,
        tierCandidates,
      );
      if (cached) {
        results[cached.key] = cached.fees;
        if (!resolvedTier) resolvedTier = cached.tier;
        continue;
      }

      let { tier, fees } = await this.loadSharedFusionFeesForTierCandidates(
        pt.name,
        tierCandidates,
      );
      // A newer allocation/tier request can share the in-flight lookup above.
      // Stop this stale roster before it dispatches another network request.
      if (!this._mounted || requestId !== this._fusionSubFeesRequestId) return;
      // Retry a missing selected sleeve once before presenting a data issue.
      // The rest of the roster remains serial and is not retried in bulk.
      if (!fees && selectedOrder.has(pt.name)) {
        ({ tier, fees } = await this.loadSharedFusionFeesForTierCandidates(
          pt.name,
          tierCandidates,
        ));
        if (!this._mounted || requestId !== this._fusionSubFeesRequestId) return;
      }
      if (!fees) continue;
      if (!resolvedTier) {
        resolvedTier = tier;
      }
      const key = this.getFusionFeeKey(pt.name, tier);
      if (!results[key]) {
        results[key] = fees;
      }
    }
    // Discard if a newer tier/allocation change superseded this fetch.
    if (!this._mounted || requestId !== this._fusionSubFeesRequestId) return;
    const effectiveTier = resolvedTier || tierCandidates[0];
    const collapsedEffective = collapseTierLabel(effectiveTier);
    // Validation lookup tries the resolved tier, the collapsed label, and
    // finally any cached key for the strategy. Mirrors the lenient match
    // in buildFusionFeeBlocks so we don't claim "incomplete schedule" for
    // a sub-strategy whose fees did load (just under a tier label that
    // differs from effectiveTier in the IAR suffix).
    const getFeeKey = (subStrategy) =>
      resolveFusionSubFeeEntry(
        results,
        subStrategy,
        effectiveTier,
        collapsedEffective,
      )?.key || this.getFusionFeeKey(subStrategy, effectiveTier);
    const fusionFeeValidation = getFusionFeeValidation(fusionAllocations, results, getFeeKey);
    // T4 (round 4) — blended-fee computation removed for compliance: each
    // sub-strategy's full tier schedule is shown individually on the form
    // (selected only) and PDF (full eligible roster).
    this.setState({
      fusionSubFees: results,
      fusionSubFeesLoading: false,
      fusionFeeValidation,
      selectedManagementFeeTierKey: effectiveTier || this.state.selectedManagementFeeTierKey,
    });
  };

  // T4 — locate cached fees for a sub-strategy under the active tier.
  // Tier resolution is forgiving: management-fees rows in DynamoDB are
  // generally keyed by the IM tier (e.g. "Tier B"), but the ISP form's
  // selected tier may include the IAR-schedule suffix (e.g. "Tier B WK").
  // We try the exact selected tier first, then the collapsed label, then
  // any cached entry for this strategy regardless of suffix. The displayed
  // tier is always the collapsed label so the PDF reads "Tier B" rather
  // than the IAR-flavored "Tier B WK".
  resolveFusionSubFees = (strategy, exactTier, collapsed) => {
    return resolveFusionFeeTable(
      this.state.fusionSubFees || {},
      strategy,
      exactTier,
      collapsed,
    );
  };

  // T4 (form display) — per-sleeve blocks for the SELECTED Fusion
  // allocations only. Drives the form's Section 4 fee schedule list.
  // Each block carries strategy + allocation weight + tier + tieredRates.
  buildFusionFeeBlocks = () => {
    if (!this.isFusionStrategy()) return [];
    const exactTier = this.getSelectedManagementFeeTierKey();
    const collapsed = collapseTierLabel(exactTier);
    return this.getEffectiveFusionAllocations()
      .filter((a) => a && a.strategy)
      .map((a) => {
        const fees = this.resolveFusionSubFees(a.strategy, exactTier, collapsed);
        return {
          strategy: a.strategy,
          displayName:
            a.displayName ||
            getFusionAllocationDisplayName(a, this.props.lists?.proposalTypes || []),
          riskTolerance: a.riskTolerance || "",
          taxSensitivity: a.taxSensitivity || "",
          registration: a.registration || "",
          accountOwner: a.accountOwner || "",
          weight: a.percentage,
          tier: collapsed || exactTier || "",
          tieredRates: fees?.tieredRates || [],
          minimumFee: fees?.minimumFee || "",
        };
      });
  };

  getFusionAllocationRiskValidation = () => {
    const base = {
      isValid: true,
      score: null,
      category: "",
      selectedPortfolio: this.state.selectedPortfolio || "",
      message: "",
    };
    if (!this.isFusionStrategy() || this.shouldSkipRiskTolerance()) return base;

    const result = calculateFusionWeightedRiskScore(
      this.state.fusionAllocations,
      this.props.lists?.proposalTypes || [],
    );
    const summary = {
      ...base,
      score: result.score,
      category: result.category,
    };
    if (!this.isFusionAllocationSelectionValid() || !this.state.selectedPortfolio) {
      return summary;
    }
    if (result.score === null || result.hasMissingScore) {
      return {
        ...summary,
        isValid: false,
        message:
          "Fusion allocation risk score could not be calculated for every selected sub-strategy.",
      };
    }
    const matches = portfolioMatchesRiskCategory(
      this.state.selectedPortfolio,
      result.category,
      this.getRiskToleranceAliases(),
    );
    if (matches) return summary;

    return {
      ...summary,
      isValid: false,
      message: `Fusion allocation risk category (${result.category}) must match the selected Risk Tolerance (${this.state.selectedPortfolio}).`,
    };
  };

  // T4 (payload / PDF) — the FULL roster of Fusion-eligible sub-strategies,
  // one block per eligible strategy. Selected allocations carry their
  // weight; un-selected eligible sub-strategies ship with weight = "" so
  // the PDF can render their tier schedules for compliance without
  // pretending the client chose an allocation.
  buildFusionFeeBlocksForPayload = () => {
    if (!this.isFusionStrategy()) return [];
    const exactTier = this.getSelectedManagementFeeTierKey();
    const collapsed = collapseTierLabel(exactTier);
    const eligible = getEligibleFusionStrategies(
      this.props.lists?.proposalTypes || [],
      { bondType: this.state.bondType, allowIspOnly: true }
    );
    const allocationsByStrategy = new Map();
    this.getEffectiveFusionAllocations()
      .filter((a) => a && a.strategy)
      .forEach((allocation) => {
        const entries = allocationsByStrategy.get(allocation.strategy) || [];
        entries.push(allocation);
        allocationsByStrategy.set(allocation.strategy, entries);
      });
    return eligible.map((pt) => {
      const allocations = allocationsByStrategy.get(pt.name) || [];
      const allocation = allocations.length === 1 ? allocations[0] : null;
      const weight = allocations.length
        ? String(
            Number(
              allocations
                .reduce((sum, entry) => sum + (parseFloat(entry.percentage) || 0), 0)
                .toFixed(4)
            )
          )
        : "";
      const fees = this.resolveFusionSubFees(pt.name, exactTier, collapsed);
      return {
        strategy: pt.name,
        displayName:
          allocation?.displayName ||
          getFusionAllocationDisplayName(allocation || { strategy: pt.name }, this.props.lists?.proposalTypes || []),
        riskTolerance: allocation?.riskTolerance || "",
        taxSensitivity: allocation?.taxSensitivity || "",
        registration: allocation?.registration || "",
        accountOwner: allocation?.accountOwner || "",
        weight,
        tier: collapsed || exactTier || "",
        tieredRates: fees?.tieredRates || [],
        minimumFee: fees?.minimumFee || "",
      };
    });
  };

  handleFusionAllocationsChange = (allocations) => {
    this.setState({
      fusionAllocations: this.normalizeFusionAllocations(
        allocations,
        this.state.fusionRegistrationType,
      ),
    }, () => {
      this.fetchFusionSubFees();
    });
  };

  handleAccountNumberChange = (index, value) => {
    const newNumbers = [...this.state.accountNumbers];
    newNumbers[index] = value;

    // Auto-add new empty field when the last one is filled
    if (index === newNumbers.length - 1 && value.trim() !== "") {
      newNumbers.push("");
    }

    // Remove empty fields except the last one
    const filtered = newNumbers.filter(
      (num, idx) =>
        num.trim() !== "" ||
        idx === newNumbers.length - 1 ||
        newNumbers.length === 1
    );

    this.setState({ accountNumbers: filtered });
  };

  handleFeeUpdateAccountsChange = (accounts) => {
    // Preserve in-progress text exactly while the user types. Registration
    // changes are normalized by the row editor, and the submission boundary
    // performs the final canonical trim.
    const feeUpdateAccounts =
      Array.isArray(accounts) && accounts.length > 0
        ? accounts.map((account) => ({ ...account }))
        : [createEmptyFeeUpdateAccount()];
    const legacyFields = buildFeeUpdateLegacyFields(feeUpdateAccounts);
    const proposalTypes = this.props.lists?.proposalTypes || [];
    const allPcs = feeUpdateAccounts.every(
      (account) =>
        !!String(account?.strategy || "").trim() &&
        isPcsRegistrationStrategy(account.strategy, proposalTypes),
    );
    const standardStrategies = feeUpdateAccounts
      .map((account) => String(account?.strategy || "").trim())
      .filter(Boolean);
    const allStandardSameStrategy =
      standardStrategies.length === feeUpdateAccounts.length &&
      new Set(standardStrategies).size === 1 &&
      standardStrategies.every((strategy) =>
        isStandardRegistrationStrategy(strategy, proposalTypes),
      );

    if (legacyFields.strategy !== this.state.strategy) {
      this.handleFieldChange("strategy", legacyFields.strategy);
    }

    this.setState({
      feeUpdateAccounts,
      accountNumbers: legacyFields.accountNumbers,
      registrations: legacyFields.registrations,
      accountNumber: legacyFields.accountNumbers.filter(Boolean).join(", "),
      pcsRegistrationType:
        allPcs && feeUpdateAccounts.length > 1
          ? "Multi"
          : this.state.pcsRegistrationType,
      standardRegistrationType:
        allStandardSameStrategy && feeUpdateAccounts.length > 1
          ? "Multi"
          : allStandardSameStrategy
            ? this.state.standardRegistrationType
            : "Single",
      iarFeeWaiver: null,
    });
  };

  handleRegistrationChange = (registrationKey) => (e) => {
    const isStandardMultiRegistration = this.isStandardMultiRegistration();
    const newRegistrations = this.state.registrations.map((reg) => {
      if (reg.registration === registrationKey) {
        return {
          ...reg,
          value: e.target.value,
          ...(isStandardMultiRegistration
            ? { bondType: "", bondTypePreferenceProvided: false }
            : {}),
        };
      }
      return reg;
    });

    // If the new registration is Qualified and bond type is Municipal, reset to Taxable
    const conditionalReset =
      this.isPcsMultiRegistration() || isStandardMultiRegistration
        ? {}
        : getConditionalStateReset({
            requestType: this.state.requestType,
            existingRequestAction: this.state.existingRequestAction,
            registration: newRegistrations[0]?.value,
            custodian: this.state.custodian,
            jointClients: this.state.jointClients,
            registrationTypes: this.props.lists?.registrationTypes || [],
          });
    const updates = { registrations: newRegistrations, ...conditionalReset };
    if (
      Array.isArray(conditionalReset.jointClients) &&
      conditionalReset.jointClients.length < this.state.jointClients.length
    ) {
      const retained = new Set(
        conditionalReset.jointClients.map(getJointClientIdentity),
      );
      updates.deferredJointClients = mergeJointClients(
        this.state.deferredJointClients || [],
        this.state.jointClients.filter(
          (client) => !retained.has(getJointClientIdentity(client)),
        ),
      );
    } else if (
      getRegistrationRules(newRegistrations[0]?.value, this.state.custodian)
        .allowsSecondAccountHolder
    ) {
      this.restoreDeferredJointClients(updates);
    }
    const regType = getIspRegistrationTypes(
      this.props.lists?.registrationTypes || [],
      this.state.custodian,
      {
        requestType: this.state.requestType,
        existingRequestAction: this.state.existingRequestAction,
      },
    ).find((registration) => registration.name === e.target.value);
    if (!isStandardMultiRegistration && this.isExistingRiskToleranceUpdate()) {
      updates.bondType = "";
      updates.bondTypePreferenceProvided = false;
    } else if (
      !isStandardMultiRegistration &&
      regType?.["Account Type"] === "Qualified" &&
      this.state.bondType === "Municipal"
    ) {
      updates.bondType = "Taxable";
    }
    if (
      !isStandardMultiRegistration &&
      regType?.["Account Type"] === "Qualified" &&
      this.state.pathBondType === "Municipal"
    ) {
      updates.pathBondType = "Taxable";
    }
    this.setState(updates);
  };

  restoreDeferredJointClients = (updates) => {
    if (!this.state.deferredJointClients?.length) return;
    updates.jointClients = mergeJointClients(
      this.state.jointClients,
      this.state.deferredJointClients,
    );
    updates.deferredJointClients = [];
  };

  handleRegistrationOwnerChange = (registrationKey) => (event) => {
    const accountOwner = event.target.value;
    this.setState((prev) => ({
      registrations: prev.registrations.map((registration) =>
        registration.registration === registrationKey
          ? { ...registration, accountOwner }
          : registration,
      ),
    }));
  };

  handleStandardRegistrationFieldChange = (rowKey, field, value) => {
    if (this.isExistingFeeUpdate()) {
      this.handleFeeUpdateAccountsChange(
        this.state.feeUpdateAccounts.map((account) =>
          account._rowId === rowKey
            ? {
                ...account,
                [field]: value,
                ...(field === "value" ? { registration: value } : {}),
              }
            : account,
        ),
      );
      return;
    }
    this.setState((prev) => ({
      registrations: prev.registrations.map((registration) =>
        registration.registration === rowKey
          ? {
              ...registration,
              [field]: value,
              ...(field === "value"
                ? { bondType: "", bondTypePreferenceProvided: false }
                : {}),
            }
          : registration,
      ),
    }));
  };

  addRegistration = () => {
    this.setState((prev) => ({
      registrations: [
        ...prev.registrations,
        this.isStandardMultiRegistration(prev)
          ? createStandardRegistrationRow({}, prev.registrations.length)
          : {
              registration: `registration${prev.registrations.length}`,
              accountOwner: "",
              value: "",
              percentage: "100",
            },
      ],
    }));
  };

  removeRegistration = (index) => {
    this.setState((prev) => {
      if (prev.registrations.length <= 1) return null;
      const registrations = prev.registrations
        .filter((_, i) => i !== index)
        .map((registration, registrationIndex) => ({
          ...registration,
          registration: `registration${registrationIndex}`,
        }));
      return {
        registrations,
        ...(!this.hasSoloKRegistration(registrations)
          ? { soloKQuestionnaire: getEmptySoloKQuestionnaire() }
          : {}),
      };
    });
  };

  removeStandardRegistration = (index) => {
    if (this.isExistingFeeUpdate()) {
      if (this.state.feeUpdateAccounts.length <= 1) return;
      this.handleFeeUpdateAccountsChange(
        this.state.feeUpdateAccounts.filter(
          (_, accountIndex) => accountIndex !== index,
        ),
      );
      return;
    }
    this.removeRegistration(index);
  };

  handleSoloKQuestionnaireChange = (field, value) => {
    this.setState((prev) => {
      const soloKQuestionnaire = {
        ...getEmptySoloKQuestionnaire(),
        ...prev.soloKQuestionnaire,
        [field]: value,
      };
      if (
        ["ownerCount", "employeeCount"].includes(field) &&
        !shouldAskSoloKSpouseQuestion(soloKQuestionnaire)
      ) {
        soloKQuestionnaire.secondParticipantIsSpouse = "";
      }
      return { soloKQuestionnaire };
    });
  };

  handleSoloKOwnershipEvidenceToggle = (evidenceName) => {
    this.setState((prev) => {
      const current = prev.soloKQuestionnaire?.ownershipEvidence || [];
      const ownershipEvidence = current.includes(evidenceName)
        ? current.filter((evidence) => evidence !== evidenceName)
        : [...current, evidenceName];
      return {
        soloKQuestionnaire: {
          ...getEmptySoloKQuestionnaire(),
          ...prev.soloKQuestionnaire,
          ownershipEvidence,
        },
      };
    });
  };

  handleSourceOfFundsToggle = (fundName) => {
    this.setState((prev) => {
      const selected = prev.sourceOfFunds.includes(fundName)
        ? prev.sourceOfFunds.filter((f) => f !== fundName)
        : [...prev.sourceOfFunds, fundName];
      return {
        sourceOfFunds: selected,
        sourceOfFundsText: this.getSelectedSourceOfFundsText(
          selected,
          prev.sourceOfFundsText,
        ),
      };
    });
  };

  handleSourceOfFundsTextChange = (fundName, text) => {
    this.setState((prev) => ({
      sourceOfFundsText: { ...prev.sourceOfFundsText, [fundName]: text },
    }));
  };

  // --- Section 1: Joint Clients CRUD ---
  showAddClientDialog = () => {
    this.setState({
      showClientDialog: true,
      dialogEditIndex: -1,
    });
  };

  showEditClientDialog = (index) => {
    this.setState({
      showClientDialog: true,
      dialogEditIndex: index,
    });
  };

  hideClientDialog = () => {
    this.setState({ showClientDialog: false, dialogEditIndex: -1, dialogCopySource: -1 });
  };

  handleSaveClient = (clientData) => {
    this.setState((prev) => {
      if (
        !this.allowsSecondAccountHolderForState(prev) &&
        prev.dialogEditIndex < 0 &&
        prev.jointClients.length >= 1
      ) {
        return {
          showClientDialog: false,
          dialogEditIndex: -1,
          dialogCopySource: -1,
        };
      }
      const client = {
        ...clientData,
        id: prev.dialogEditIndex >= 0
          ? prev.jointClients[prev.dialogEditIndex].id
          : `client_${Date.now()}`,
      };

      let jointClients;
      if (prev.dialogEditIndex >= 0) {
        jointClients = prev.jointClients.map((c, i) =>
          i === prev.dialogEditIndex ? client : c
        );
      } else {
        jointClients = [...prev.jointClients, client];
      }

      // If this client is marked primary, unmark all others
      if (client.isPrimary) {
        jointClients = jointClients.map((c) =>
          c.id === client.id ? c : { ...c, isPrimary: false }
        );
      }

      // Keep the primary client at the top of the list so the displayed order
      // (and everything derived from it) always leads with the primary holder.
      jointClients = [
        ...jointClients.filter((c) => c.isPrimary),
        ...jointClients.filter((c) => !c.isPrimary),
      ];

      return { jointClients, showClientDialog: false, dialogEditIndex: -1, dialogCopySource: -1 };
    });
  };

  copyClient = (index) => {
    this.setState({
      showClientDialog: true,
      dialogEditIndex: -1,
      dialogCopySource: index,
    });
  };

  removeClient = (index) => {
    const client = this.state.jointClients[index];
    const name = [client?.firstName, client?.lastName].filter(Boolean).join(" ") || "this client";
    this.setState({
      confirmModal: {
        title: "Remove Client",
        message: `Are you sure you want to remove ${name}?`,
        onConfirm: () => this.setState((prev) => {
          const jointClients = prev.jointClients.filter((_, i) => i !== index);
          return { jointClients, confirmModal: null };
        }),
      },
    });
  };

  // Primary client first, joint clients in original entry order. Used for
  // display, payload serialization, and combined-name building so the PDF
  // never shows a co-holder ahead of the primary.
  getOrderedJointClients = () => {
    const { jointClients } = this.state;
    const primary = jointClients.filter((c) => c.isPrimary);
    const others = jointClients.filter((c) => !c.isPrimary);
    return [...primary, ...others];
  };

  // Build combined client name string for display and proposal pre-fill
  // Same last name: "John & Jane Smith"
  // Different last names: "John Smith & Jane Doe"
  // Three+ clients: "John A. Smith & Jane Doe & Bob Smith"
  getCombinedClientName = () => {
    const jointClients = this.getAllowedJointClients();
    if (jointClients.length === 0) return "";
    if (jointClients.length === 1) return jointClients[0].clientName;

    // Extract last names for comparison
    const clients = jointClients.map((c) => {
      const parts = c.clientName.trim().split(/\s+/);
      const last = parts.length > 1 ? parts[parts.length - 1] : "";
      const rest = parts.length > 1 ? parts.slice(0, -1).join(" ") : parts[0];
      return { first: rest, last, full: c.clientName };
    });

    // Check if all clients share the same last name
    const allSameLast = clients.every(
      (c) => c.last && c.last.toLowerCase() === clients[0].last.toLowerCase()
    );

    if (allSameLast && clients[0].last) {
      // "John & Jane Smith" or "John & Jane & Bob Smith"
      const firsts = clients.map((c) => c.first).join(" & ");
      return `${firsts} ${clients[0].last}`;
    }

    // Different last names: "John Smith & Jane Doe"
    return clients.map((c) => c.full).join(" & ");
  };

  isQualifiedRegistrationSelected = () => {
    const selectedRegistration = this.isFusionMultiRegistration()
      ? ""
      : this.state.registrations?.[0]?.value;
    if (!selectedRegistration) return false;

    const registrationType = getIspRegistrationTypes(
      this.props.lists?.registrationTypes || [],
      this.state.custodian,
      {
        requestType: this.state.requestType,
        existingRequestAction: this.state.existingRequestAction,
      },
    ).find((registration) => registration.name === selectedRegistration);
    return registrationType?.["Account Type"] === "Qualified";
  };

  // Check if selected strategy supports both bond types AND registration is Non-Qualified
  hasBondOptions = () => {
    const { strategy } = this.state;
    if (!strategy) return false;
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === strategy);
    if (
      !pt ||
      pt.setBondValueBlank === true ||
      !Array.isArray(pt.taxableBonds) ||
      pt.taxableBonds.length === 0 ||
      !Array.isArray(pt.municipalBonds) ||
      pt.municipalBonds.length === 0
    ) return false;

    // Qualified accounts (retirement, HSA) are always taxable — hide the bond type choice
    if (this.isQualifiedRegistrationSelected()) return false;

    return true;
  };

  // Get risk tolerance options from strategy's bond-type arrays (same as proposal generator)
  getStrategyDescription = () => {
    const { strategy } = this.state;
    if (!strategy) return "";
    if (this.isManagedMoneyMarketStrategy(strategy)) {
      return getMmmsStrategyDescription();
    }
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === strategy);
    return (
      pt?.description ||
      `Client is choosing to participate in the ${this.getStrategyDisplayName(
        strategy,
      )} program under this Agreement.`
    );
  };

  getStrategyDisplayName = (strategy = this.state.strategy) =>
    getProgramDisplayName(strategy, this.props.lists?.proposalTypes);

  getFusionStrategyDescriptions = () => {
    if (!this.isFusionStrategy()) return [];
    const proposalTypes = this.props.lists?.proposalTypes || [];
    const seen = new Set();
    return (this.state.fusionAllocations || [])
      .map((allocation) => allocation?.strategy)
      .filter(Boolean)
      .filter((strategy) => {
        if (seen.has(strategy)) return false;
        seen.add(strategy);
        return true;
      })
      .map((strategy) => {
        const pt = proposalTypes.find((p) => p.name === strategy);
        const displayName = getProgramDisplayName(strategy, proposalTypes);
        return {
          strategy,
          displayName,
          description:
            pt?.description ||
            `Client is choosing to participate in the ${displayName} program under this Agreement.`,
        };
      });
  };

  getRiskToleranceOptions = (overrides = {}) => {
    const { strategy, bondType, modelSelection } = { ...this.state, ...overrides };
    if (!strategy) return [];
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === strategy);
    if (!pt) return [];
    if (this.isManagedMoneyMarketStrategy(strategy)) {
      const selection = canonicalizeMmmsFundSelection(modelSelection, bondType);
      return selection ? [selection] : [];
    }
    const formatActiveIncomeOption = (option) => {
      if (!String(strategy).toLowerCase().includes("active income")) {
        return option;
      }
      if (option === "Conservative") return "Conservative - 4% Income Target";
      if (option === "Moderate") return "Moderate - 5% Income Target";
      return option;
    };
    const formatOptions = (options) => options.map(formatActiveIncomeOption);

    if (this.isExistingRiskToleranceUpdate() || this.isExistingModelChange()) {
      const allBonds = [];
      for (const key of Object.keys(pt)) {
        if (key.endsWith("Bonds") && Array.isArray(pt[key])) {
          allBonds.push(...pt[key]);
        }
      }
      return formatOptions([...new Set(allBonds)]);
    }

    let bonds = [];
    if (pt.taxableBonds && pt.municipalBonds) {
      bonds = bondType === "Municipal" ? pt.municipalBonds : pt.taxableBonds;
    } else if (pt.taxableBonds) {
      bonds = pt.taxableBonds;
    } else if (pt.municipalBonds) {
      bonds = pt.municipalBonds;
    } else {
      for (const key of Object.keys(pt)) {
        if (key.endsWith("Bonds") && Array.isArray(pt[key])) {
          bonds = pt[key];
          break;
        }
      }
    }

    // When a strategy defines modelSelection (e.g. "Program Selection" on Core
    // Bond Strategies), narrow the portfolio list to options matching the chosen
    // model. Convention: model option name (minus a trailing " Strategy") is a
    // prefix of — or equal to — the portfolio name. If no options match, fall
    // back to the unfiltered list.
    if (pt.modelSelection && modelSelection && bonds.length) {
      const narrowed = narrowBondsByModel(bonds, modelSelection);
      if (narrowed.length) return formatOptions(narrowed);
    }

    return formatOptions(bonds);
  };

  getRiskToleranceAliases = () => {
    const pt = this.props.lists?.proposalTypes?.find(
      (p) => p.name === this.state.strategy
    );
    return pt?.riskToleranceAliases || {};
  };

  // Which bond types (Taxable / Municipal) have a portfolio matching the chosen
  // program for the current strategy. Drives the Bond Type lock: when a program
  // only exists under one bond type (e.g. Government Money Market is taxable
  // only), the form forces that bond type and disables the other radio. Data
  // derived — no strategy-name special-casing — so any asymmetric strategy
  // benefits. With no model chosen, every populated bond array is available.
  getAvailableBondTypesForModel = (modelSelection = this.state.modelSelection) => {
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === this.state.strategy);
    if (!pt) return [];
    if (this.isManagedMoneyMarketStrategy()) {
      const selection = canonicalizeMmmsFundSelection(
        modelSelection,
        this.state.bondType,
      );
      const bondType = getMmmsFundBondType(selection);
      return bondType ? [bondType] : [];
    }
    if (this.isExistingRiskToleranceUpdate()) {
      const primaryTypes = this.getBondTypesForStrategyName(pt.name);
      if (
        primaryTypes.includes("Taxable") &&
        primaryTypes.includes("Municipal")
      ) {
        return primaryTypes;
      }
      const pathTypes = this.getBondTypesForStrategyName(
        this.getPathStrategyName(),
      );
      if (
        pathTypes.includes("Taxable") &&
        pathTypes.includes("Municipal")
      ) {
        return pathTypes;
      }
    }
    const has = (bonds) =>
      Array.isArray(bonds) &&
      bonds.length > 0 &&
      (!modelSelection || narrowBondsByModel(bonds, modelSelection).length > 0);
    const types = [];
    if (has(pt.taxableBonds)) types.push("Taxable");
    if (has(pt.municipalBonds)) types.push("Municipal");
    return types;
  };

  // Bond types (Taxable / Municipal) available for an arbitrary strategy by
  // name — used to inspect an Elements row's path target. Unlike
  // getAvailableBondTypesForModel this ignores model selection: the path
  // strategy has no chosen model in this form.
  getBondTypesForStrategyName = (name) => {
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === name);
    if (!pt) return [];
    const types = [];
    if (Array.isArray(pt.taxableBonds) && pt.taxableBonds.length > 0) types.push("Taxable");
    if (Array.isArray(pt.municipalBonds) && pt.municipalBonds.length > 0) types.push("Municipal");
    return types;
  };

  // Risk Tolerance Updates carry an optional preference only for the same
  // non-qualified registrations that receive the choice in full workflows.
  // Elements rows are applicable when their configured path
  // strategy offers both Taxable and Municipal choices even though the
  // initial Elements stage itself is taxable-only.
  hasRiskToleranceUpdateBondPreference = () => {
    if (!this.isExistingRiskToleranceUpdate()) return false;
    if (this.isQualifiedRegistrationSelected()) return false;
    const proposalTypes = this.props.lists?.proposalTypes || [];
    const selected = proposalTypes.find(
      (proposalType) => proposalType.name === this.state.strategy,
    );
    if (!selected || selected.setBondValueBlank === true) return false;

    const hasBothBondTypes = (proposalType) => {
      if (!proposalType || proposalType.setBondValueBlank === true) return false;
      const types = this.getBondTypesForStrategyName(proposalType.name);
      return types.includes("Taxable") && types.includes("Municipal");
    };
    if (hasBothBondTypes(selected)) return true;

    const pathStrategy = this.getPathStrategyName();
    const path = proposalTypes.find(
      (proposalType) => proposalType.name === pathStrategy,
    );
    return hasBothBondTypes(path);
  };

  shouldSuppressPathStrategy = (proposalType) => {
    return shouldSuppressElementsPathStrategy(proposalType?.name);
  };

  // The path/graduation strategy name configured on the selected strategy
  // row (proposal-types `pathStrategy`), or "" when none.
  getPathStrategyName = () => {
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === this.state.strategy);
    if (this.shouldSuppressPathStrategy(pt)) return "";
    return pt?.pathStrategy || "";
  };

  getPathBondTypes = () => {
    const name = this.getPathStrategyName();
    return name ? this.getBondTypesForStrategyName(name) : [];
  };

  // Show the path bond radio only when the target offers a real choice
  // (both Taxable and Municipal). Taxable-only targets need no selection.
  hasPathBondChoice = () => {
    const types = this.getPathBondTypes();
    return types.includes("Taxable") && types.includes("Municipal");
  };

  // True when the selected strategy is flat AND a path-strategy row exists in
  // the loaded management fees. Used to gate path-stage IAR rendering and
  // validation. Falls back to false during the initial render before
  // managementFees / pathStrategyFees load.
  hasPath = () => {
    const mgmt = this.state.managementFees;
    if (!mgmt || mgmt.flatFee == null) return false;
    if (!this.getPathStrategyName()) return false;
    // The frontend loads the path strategy's mgmt-fees alongside the primary
    // strategy (in the existing fees-fetch path that injected pathMinimumFee).
    // The presence of that fetched row is the source of truth for "path resolves."
    return !!(this.state.managementFees?.pathStrategyRow);
  };

  isFlatFeeOnlyStrategy = () => {
    const mgmt = this.state.managementFees;
    return !!(mgmt && mgmt.flatFee != null && !this.hasPath() && !this.isFusionStrategy());
  };

  hasInitialFeeStage = () => {
    const mgmt = this.state.managementFees;
    return !!(
      mgmt &&
      mgmt.flatFee != null &&
      isElementsStrategyName(this.state.strategy) &&
      !this.isFusionStrategy()
    );
  };

  getElementsManagementFeeSnapshot = () => {
    if (!this.hasInitialFeeStage()) return undefined;
    const fees = this.state.managementFees;
    if (!fees) return undefined;

    const snapshot = { flatFee: fees.flatFee };
    if (fees.minimumFee != null) snapshot.minimumFee = fees.minimumFee;
    if (Array.isArray(fees.tieredRates)) {
      snapshot.tieredRates = fees.tieredRates;
    }
    if (fees.pathMinimumFee != null) {
      snapshot.pathMinimumFee = fees.pathMinimumFee;
    }
    return snapshot;
  };

  isManagedMoneyMarketStrategy = (strategy = this.state.strategy) =>
    isManagedMoneyMarketStrategyName(strategy);

  getEffectiveIarFeeType = () => {
    if (this.hasPath() || this.hasInitialFeeStage()) {
      return this.state.iarFeeType || "Fixed Percentage";
    }
    if (this.isFlatFeeOnlyStrategy()) return "Fixed Percentage";
    return this.state.iarFeeType;
  };

  getIarFeeMax = () =>
    this.isManagedMoneyMarketStrategy()
      ? 0.15
      : 2;

  getRiskToleranceDescription = (category) => {
    const BASE_DESCRIPTIONS = {
      Conservative:
        "Emphasis on generating a stable level of current income. Future capital appreciation is a secondary objective. Modest annual principal fluctuation is expected and acceptable.",
      Moderate:
        "Emphasis on both current income and future capital appreciation. Principal risk and fluctuation are expected and acceptable.",
      Growth:
        "Emphasis is entirely on future capital appreciation. Substantial principal risk and fluctuation are expected and acceptable.",
    };

    if (!category) return "";

    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === this.state.strategy);

    // Strategy-level overrides (e.g., Fee-Based Annuity, Core Bond)
    if (pt?.riskToleranceDescriptions?.[category]) {
      return pt.riskToleranceDescriptions[category];
    }

    const base = BASE_DESCRIPTIONS[category] || "";

    // Derive options from the already-found pt to avoid a second .find()
    const options = this.getRiskToleranceOptions();
    if (!options.length) return base;

    // Extract equity allocations for this category from portfolio names like "Conservative 30/70"
    const catLower = category.toLowerCase();
    const allocations = [];
    for (const opt of options) {
      if (!opt.toLowerCase().startsWith(catLower)) continue;
      const match = opt.match(/(\d+)\/(\d+)/);
      if (match) allocations.push(parseInt(match[1], 10));
    }

    if (!allocations.length) return base;

    allocations.sort((a, b) => a - b);
    const pctList = allocations.map((a) => `${a}%`);
    const allocationText = pctList.length === 1
      ? `The portfolio in this category offers an equity allocation of ${pctList[0]}.`
      : `The portfolios in this category offer equity allocations of ${pctList.slice(0, -1).join(", ")}, and ${pctList[pctList.length - 1]} respectively.`;

    return `${base}  ${allocationText}`;
  };

  // --- Section 3: Risk Questionnaire ---
  handleRiskAnswerChange = (section, questionId, score) => {
    this.setState((prev) => {
      const answersKey = `${section}Answers`;
      const answers = { ...prev[answersKey], [questionId]: score };

      const capAnswers = section === "capacity" ? answers : prev.capacityAnswers;
      const willAnswers = section === "willingness" ? answers : prev.willingnessAnswers;
      const { capacityTotal, willingnessTotal, combinedScore, riskCategory } = computeRiskScores(capAnswers, willAnswers);

      let selectedPortfolio = prev.selectedPortfolio;
      if (
        riskCategory !== prev.riskCategory &&
        !prev.portfolioOverride &&
        !this.isManagedMoneyMarketStrategy(prev.strategy)
      ) {
        selectedPortfolio = "";
      }

      return {
        [answersKey]: answers,
        capacityTotal,
        willingnessTotal,
        combinedScore,
        riskCategory,
        selectedPortfolio,
      };
    });
  };

  handlePortfolioChange = (value) => {
    this.setState({ selectedPortfolio: value });
  };

  handleOverrideToggle = (checked) => {
    this.setState({
      portfolioOverride: checked,
      selectedPortfolio: "",
    });
  };

  handleOverrideReasonChange = (value) => {
    this.setState({ overrideReason: value });
  };

  handleOverrideOtherTextChange = (value) => {
    this.setState({ overrideOtherText: value });
  };

  handleRiskReset = () => {
    this.setState({
      capacityAnswers: {},
      willingnessAnswers: {},
      capacityTotal: 0,
      willingnessTotal: 0,
      combinedScore: 0,
      riskCategory: "",
      selectedPortfolio: this.isManagedMoneyMarketStrategy()
        ? this.state.modelSelection
        : "",
      portfolioOverride: false,
      overrideReason: "",
      overrideOtherText: "",
    });
  };

  renderAllocationOnlyRiskSelection = () => {
    const options = this.getRiskToleranceOptions();
    return (
      <div>
        <div className="alert alert-info py-2">
          Model Change requests do not require the Risk Tolerance Questionnaire.
          Select the new allocation below.
        </div>
        <div className="form-group">
          <label htmlFor="modelChangeAllocation">
            <strong>Risk Tolerance / Allocation</strong>
          </label>
          <select
            id="modelChangeAllocation"
            className="form-control"
            value={this.state.selectedPortfolio || ""}
            onChange={(e) => this.handlePortfolioChange(e.target.value)}
            style={{
              borderLeftColor: this.state.selectedPortfolio
                ? "green"
                : "#ced4da",
              borderLeftWidth: "5px",
            }}
          >
            <option value="">Select Allocation</option>
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  // Navigate to proposal step guide with ISP data pre-filled
  navigateToCreateProposal = () => {
    const clientName = this.getCombinedClientName();
    const isStandardMulti = this.isStandardMultiRegistration();
    const standardRows = isStandardMulti
      ? this.state.existingRequestAction === "Fee Update"
        ? (this.state.feeUpdateAccounts || []).map((account, index) =>
            createStandardRegistrationRow(
              {
                ...account,
                value: account.registration,
              },
              index,
            ),
          )
        : normalizeStandardRegistrationRows(this.state.registrations)
      : [];
    const effectiveInvestmentAmount = isStandardMulti
      ? standardRows.reduce(
          (total, row) =>
            total +
            (parseFloat(
              String(row.initialInvestmentAmount || "").replace(
                /[^0-9.-]+/g,
                "",
              ),
            ) || 0),
          0,
        )
      : parseFloat(
          String(this.state.initialInvestmentAmount).replace(/[^0-9.-]+/g, ""),
        ) || 0;
    const effectiveInvestmentDisplay =
      effectiveInvestmentAmount > 0
        ? `$${effectiveInvestmentAmount.toLocaleString()}`
        : "";

    const isFusion = this.state.strategy &&
      this.state.strategy.toLowerCase().includes("fusion");

    const ispProposalData = {
      DateTime: this.state.submissionDateTime || "",
      program: this.state.strategy,
      "CLIENT NAMES": clientName,
      "ADVISOR NAME": this.state.advisorName,
      customTemplate: this.state.advisorName,
      "RISK TOLERANCE": this.state.selectedPortfolio,
      ispRiskCategory: this.state.riskCategory,
      ispSelectedPortfolio: this.state.selectedPortfolio,
      optionsTM: this.state.bondType,
      assetValue: effectiveInvestmentDisplay,
      registrations: isStandardMulti
        ? standardRows
        : this.state.registrations,
      incomeNeeds: "",
      logoSize: "",
      logoOffset: "",
      papercheck: "true",
      othercheck: "",
      insertType: "",
      fusionAccountNumbers: isFusion
        ? [this.state.accountNumber].filter((n) => String(n || "").trim())
        : [],
      isNewFusionClient: this.state.requestType === "New",
      ispClients: this.getAllowedJointClients().map((c) => ({
        name: c.clientName,
        isPrimary: c.isPrimary,
      })),
      ispAmount: isStandardMulti
        ? effectiveInvestmentDisplay
        : this.state.initialInvestmentAmount,
      ispGoal: this.getAccountGoalDisplay(),
      ispFusionAllocations: isFusion
        ? this.getEffectiveFusionAllocations()
        : [],
    };

    if (this.props.navigateToProposalFromISP) {
      this.props.navigateToProposalFromISP(ispProposalData);
    } else {
      sessionStorage.setItem("ispProposalData", JSON.stringify(ispProposalData));
      window.location.href = "/proposals/step-guide";
    }
  };

  buildSubmissionPayload = () => {
    const incomeYes = this.state.accountIncomeNeed === "Yes";
    const hasOverride = this.state.portfolioOverride;
    const skipRiskTolerance = this.shouldSkipRiskTolerance();
    const skipRiskQuestionnaireAnswers =
      this.shouldSkipRiskQuestionnaireAnswers();
    const selectedPortfolioCategory = getPortfolioRiskCategory(
      this.state.selectedPortfolio,
      this.getRiskToleranceAliases(),
    );

    // Build risk Q&A array from the standard question sets
    const riskQA = skipRiskQuestionnaireAnswers
      ? []
      : [...CAPACITY_QUESTIONS, ...WILLINGNESS_QUESTIONS].map((q) => {
        const section = q.id.startsWith("cap") ? "capacity" : "willingness";
        const answers = this.state[`${section}Answers`];
        const selectedScore = answers[q.id];
        const selectedAnswer = q.answers.find((a) => a.score === selectedScore);
        return { q: q.text, a: selectedAnswer?.text || "", score: selectedScore };
      });

    // Build risk tolerance description: tied to Risk Allocation (post-override) rather
    // than the questionnaire category, so an overridden portfolio drives the wording.
    // Portfolio names start with the category ("Conservative 30/70", etc.), so the
    // first whitespace-separated token is the category key used by BASE_DESCRIPTIONS.
    const effectiveCategory =
      (this.state.portfolioOverride || this.isExistingModelChange()) &&
      this.state.selectedPortfolio
        ? selectedPortfolioCategory || this.state.selectedPortfolio.split(" ")[0]
        : this.state.riskCategory;
    const riskToleranceDescription = skipRiskTolerance
      ? ""
      : this.getRiskToleranceDescription(effectiveCategory);

    // Final bond-type resolution: when the selected strategy carries
    // setBondValueBlank === true, clear the bond type so it isn't recorded.
    // Any other (falsy) value leaves the chosen bond type untouched.
    const selectedStrategy = this.props.lists?.proposalTypes?.find(
      (p) => p.name === this.state.strategy
    );
    const bondType =
      selectedStrategy?.setBondValueBlank === true ? "" : this.state.bondType;
    const bondTypePromptShown = this.isBondTypePromptShown();
    const bondPreference = getBondPreferencePayload(
      bondType,
      this.isBondTypePreferenceProvided(),
    );
    const bondTypePreferenceProvided = bondPreference.bondTypePreferenceProvided;
    const includeFullAccountInfo = !this.isReducedAccountInfoUpdate();
    const includeHouseholding = this.shouldAskHouseholding();
    const isPcsMultiRegistration = this.isPcsMultiRegistration();
    const isStandardMultiRegistration =
      this.isStandardMultiRegistration();
    const ignorePrimaryRegistration =
      this.isFusionMultiRegistration() ||
      isPcsMultiRegistration ||
      isStandardMultiRegistration;
    const effectiveIarFeeType = this.getEffectiveIarFeeType();
    const feeUpdateAccounts = this.isExistingFeeUpdate()
      ? normalizeFeeUpdateAccounts(
          {
            feeUpdateAccounts: this.state.feeUpdateAccounts,
          },
          {
            registrationTypes: this.props.lists?.registrationTypes || [],
          },
        ).map(({ _rowId, ...account }) => account)
      : [];
    const feeUpdateLegacyFields = buildFeeUpdateLegacyFields(
      feeUpdateAccounts.length > 0
        ? feeUpdateAccounts
        : [createEmptyFeeUpdateAccount()],
    );
    const feeUpdateStrategies = Array.from(
      new Set(feeUpdateAccounts.map((account) => account.strategy).filter(Boolean)),
    );
    const submissionStrategy =
      this.isExistingFeeUpdate() && feeUpdateStrategies.length > 1
        ? "Multiple Strategies"
        : feeUpdateStrategies[0] || this.state.strategy;
    const pcsRegistrationType =
      this.isPcsRegistrationTypeEligibleForState()
        ? this.state.pcsRegistrationType || "Single"
        : "";
    const pcsRegistrations =
      pcsRegistrationType === "Multi"
        ? this.isExistingFeeUpdate()
          ? feeUpdateAccounts.map((account) => ({
              accountOwner: String(account.accountOwner || "").trim(),
              registration: String(account.registration || "").trim(),
            }))
          : buildPcsRegistrationPayload(this.state.registrations)
        : [];
    const standardRegistrationType =
      this.isStandardRegistrationTypeEligibleForState()
        ? this.state.standardRegistrationType || "Single"
        : "";
    const standardRegistrations =
      standardRegistrationType === "Multi"
        ? buildStandardRegistrationPayload(
            this.isExistingFeeUpdate()
              ? feeUpdateAccounts
              : this.state.registrations,
          )
        : [];
    const usesTieredIarSchedule =
      effectiveIarFeeType === "Tiered" ||
      (this.hasPath() && this.state.pathIarFeeType === "Tiered");
    const authorizedIarFeeSchedule = resolveIarFeeSchedule(
      this.state.customFees,
      this.state.iarFeeScheduleType,
    );
    const primaryRegistration =
      ignorePrimaryRegistration || this.isExistingFeeUpdate()
      ? ""
      : this.state.registrations?.[0]?.value || "";
    const registrationDetails = normalizeRegistrationDetails(
      this.state,
      primaryRegistration,
      this.props.lists?.registrationTypes || [],
    );

    const payload = {
      submissionType: "ISP",
      advisorName: this.state.advisorName,
      custodian: includeFullAccountInfo ? this.state.custodian : "",
      strategy: submissionStrategy,
      strategyDisplayName:
        submissionStrategy === "Multiple Strategies"
          ? submissionStrategy
          : this.getStrategyDisplayName(),
      feeContextStrategy:
        this.isExistingFeeUpdate() ? feeUpdateStrategies[0] || "" : "",
      feeUpdateAccounts:
        this.isExistingFeeUpdate() ? feeUpdateAccounts : undefined,
      feeUpdateStrategyFeeBlocks:
        this.isExistingFeeUpdate() && feeUpdateStrategies.length > 1
          ? this.state.feeUpdateStrategyFeeBlocks
          : undefined,
      ispFundingThreshold: getIspFundingThreshold(this.state.strategy),
      hasInitialFeeStage: this.hasInitialFeeStage(),
      hasPath: this.hasPath(),
      // Keep the internal bond category for portfolio/MMMS processing. The
      // explicit prompt signal controls whether Review/PDF may present it.
      bondType: isStandardMultiRegistration
        ? ""
        : this.isExistingRiskToleranceUpdate()
        ? bondTypePreferenceProvided
          ? bondPreference.bondType
          : ""
        : includeFullAccountInfo
        ? bondType
        : "",
      bondTypePromptShown:
        isStandardMultiRegistration ? false : bondTypePromptShown,
      bondTypePreferenceProvided:
        isStandardMultiRegistration ? false : bondTypePreferenceProvided,
      pathStrategy: this.getPathStrategyName(),
      pathBondType:
        includeFullAccountInfo &&
        !isStandardMultiRegistration &&
        this.hasPathBondChoice()
          ? this.state.pathBondType
          : "",
      // Path-stage IAR fee — only meaningful when path-mode is active. Sending
      // these unconditionally is fine; the Lambda only consumes them when the
      // mgmt-fee row has flatFee AND the path row exists.
      pathIarFeeType: this.state.pathIarFeeType,
      pathIarFeeFlat: this.state.pathIarFeeFlat,
      pathIarFeeTiered: this.state.pathIarFeeTiered,
      modelSelection: includeFullAccountInfo ? this.state.modelSelection : "",
      registration: isPcsMultiRegistration || isStandardMultiRegistration
        ? ""
        : this.isExistingFeeUpdate()
        ? feeUpdateLegacyFields.registrations
            .map((registration) => registration.value)
            .filter(Boolean)
            .join(", ")
        : this.getRegistrationDisplay(),
      registrations: this.isExistingFeeUpdate()
        ? isPcsMultiRegistration || isStandardMultiRegistration
          ? feeUpdateLegacyFields.registrations.map((registration, index) => ({
              ...registration,
              accountOwner: feeUpdateAccounts[index]?.accountOwner || "",
              ...(isStandardMultiRegistration
                ? createStandardRegistrationFields(feeUpdateAccounts[index])
                : {}),
            }))
          : feeUpdateLegacyFields.registrations
        : isPcsMultiRegistration
          ? normalizePcsRegistrationRows(this.state.registrations)
        : isStandardMultiRegistration
          ? normalizeStandardRegistrationRows(this.state.registrations)
        : ignorePrimaryRegistration
          ? []
          : this.getSingleRegistrationState(),
      fusionRegistrationType: this.isFusionStrategy()
        ? this.state.fusionRegistrationType || "Single"
        : "",
      pcsRegistrationType,
      pcsRegistrations,
      standardRegistrationType,
      standardRegistrations,
      fusionRebalanceType: this.isFusionStrategy()
        ? this.getFusionRebalanceType()
        : "",
      requestType: this.state.requestType,
      existingRequestAction: this.state.existingRequestAction,
      accountNumber: this.isExistingFeeUpdate()
        ? feeUpdateLegacyFields.accountNumbers.filter(Boolean).join(", ")
        : this.state.accountNumber,
      clientProfileImaSubmitted: this.shouldAskClientProfileIma()
        ? this.state.clientProfileImaSubmitted
        : "",
      // T19 — when householding is disallowed for the strategy, omit
      // household values from the payload so the PDF / DynamoDB don't record
      // a Yes/No response for a strategy that can't household. Deferred
      // strategies still collect and render Yes/NA after account graduation.
      householding:
        includeHouseholding
          ? this.state.householding
          : "",
      householdingMode: this.getStrategyHouseholdingMode(),
      householdRefName:
        includeHouseholding &&
        this.state.householding === "Yes"
          ? this.state.householdRefName
          : "",
      householdRefLast4:
        includeHouseholding &&
        this.state.householding === "Yes"
          ? this.state.householdRefLast4
          : "",
      // householdRef preserved for backwards compatibility with older
      // submissions; new submissions always populate the split fields above.
      householdRef:
        includeHouseholding &&
        this.state.householding === "Yes"
          ? this.state.householdRefLast4 || this.state.householdRef
          : "",
      householdReason:
        includeHouseholding &&
        this.state.householding === "No"
          ? this.getHouseholdReasonText()
          : "",
      householdReasonType: includeHouseholding ? this.state.householdReasonType : "",
      householdReasonOther: includeHouseholding ? this.state.householdReasonOther : "",
      initialInvestmentAmount: includeFullAccountInfo && !isStandardMultiRegistration
        ? this.state.initialInvestmentAmount
        : "",
      accountNetWorthPct: includeFullAccountInfo && !isStandardMultiRegistration
        ? this.state.accountNetWorthPct
        : "",
      accountIncomeNeed: includeFullAccountInfo && !isStandardMultiRegistration
        ? this.state.accountIncomeNeed
        : "",
      accountIncomeAmount:
        includeFullAccountInfo && !isStandardMultiRegistration && incomeYes
          ? this.state.accountIncomeAmount
          : "",
      accountIncomeStartDate:
        includeFullAccountInfo && !isStandardMultiRegistration && incomeYes
          ? this.state.accountIncomeStartDate
          : "",
      accountIncomeEndDate:
        includeFullAccountInfo && !isStandardMultiRegistration && incomeYes
          ? this.state.accountIncomeEndDate
          : "",
      accountIncomeFrequency:
        includeFullAccountInfo && !isStandardMultiRegistration && incomeYes
          ? this.state.accountIncomeFrequency
          : "",
      accountGoal:
        includeFullAccountInfo && !isStandardMultiRegistration
          ? this.getAccountGoalForPayload()
          : "",
      accountGoalOther:
        includeFullAccountInfo && !isStandardMultiRegistration
          ? this.getAccountGoalOtherForPayload()
          : "",
      sourceOfFunds: includeFullAccountInfo ? this.state.sourceOfFunds : [],
      sourceOfFundsText: includeFullAccountInfo
        ? this.getSelectedSourceOfFundsText()
        : {},
      soloKQuestionnaire:
        registrationDetails.registrationDetailKind === "soloK"
          ? registrationDetails.soloKQuestionnaire
          : undefined,
      // T15 — only ship UGMA/UTMA fields when applicable; backend uses these
      // to drive a conditional row on the PDF.
      minorName: "",
      relationshipToCustodian: "",
      registrationDetailKind: registrationDetails.registrationDetailKind,
      custodianFullName: registrationDetails.custodianFullName,
      relationshipToPrimary: registrationDetails.relationshipToPrimary,
      // Compatibility mirror for consumers that still read the former key.
      custodialAdultName: registrationDetails.custodianFullName,
      trustName: registrationDetails.trustName,
      businessName: registrationDetails.businessName,
      businessEinLast4: registrationDetails.businessEinLast4,
      decedentName: registrationDetails.decedentName,
      decedentSsnLast4: registrationDetails.decedentSsnLast4,
      relationshipToExecutor: registrationDetails.relationshipToExecutor,
      jointClients: this.getAllowedJointClients(),
      riskScores: skipRiskTolerance
        ? this.isManagedMoneyMarketStrategy()
          ? {
            selectedPortfolio: this.state.selectedPortfolio,
            portfolioOverride: false,
            overrideReason: "",
            overrideOtherText: "",
            capacityAnswers: {},
            willingnessAnswers: {},
          }
          : {}
        : this.isExistingModelChange()
        ? {
          capacityTotal: "",
          willingnessTotal: "",
          combinedScore: "",
          riskCategory: selectedPortfolioCategory || effectiveCategory || "",
          selectedPortfolio: this.state.selectedPortfolio,
          selectedPortfolioRiskCategory: selectedPortfolioCategory || "",
          portfolioOverride: false,
          overrideReason: "",
          overrideOtherText: "",
          capacityAnswers: {},
          willingnessAnswers: {},
        }
        : {
          capacityTotal: this.state.capacityTotal,
          willingnessTotal: this.state.willingnessTotal,
          combinedScore: this.state.combinedScore,
          riskCategory: this.state.riskCategory,
          selectedPortfolio: this.state.selectedPortfolio,
          selectedPortfolioRiskCategory: selectedPortfolioCategory || "",
          portfolioOverride: this.state.portfolioOverride,
          overrideReason: hasOverride ? this.state.overrideReason : "",
          overrideOtherText: hasOverride && this.state.overrideReason === "Other" ? this.state.overrideOtherText : "",
          capacityAnswers: this.state.capacityAnswers,
          willingnessAnswers: this.state.willingnessAnswers,
        },
      iarFeeTier: this.state.iarFeeTier,
      managementFeeTierKey: this.getSelectedManagementFeeTierKey(),
      // Preserve the Elements fee snapshot used for this submission so the
      // read-only ISP Details review can reproduce the signed fee values.
      managementFees: this.getElementsManagementFeeSnapshot(),
      iarFeeType: effectiveIarFeeType,
      iarFeeFlat: this.state.iarFeeFlat,
      iarFeeTiered: this.state.iarFeeTiered,
      iarFeeScheduleType: usesTieredIarSchedule
        ? authorizedIarFeeSchedule.selectedName
        : "",
      iarFeeWaiver: this.state.iarFeeWaiver,
      fusionAllocations: this.isFusionStrategy()
        ? this.getEffectiveFusionAllocations()
        : undefined,
      fusionStrategyDescriptions: this.getFusionStrategyDescriptions(),
      // T4 — per-sleeve schedules replace the blended computation. The
      // payload variant ships the FULL eligible-Fusion-sub-strategy list
      // (with weight only on selected allocations) so the PDF can render
      // every eligible sub-strategy's tier schedule for compliance.
      fusionFeeBlocks: this.isFusionStrategy() ? this.buildFusionFeeBlocksForPayload() : undefined,
      riskQA,
      riskToleranceDescription,
      additionalAdvisors: this.state.additionalAdvisors,
      accountNumbers: this.isExistingFeeUpdate()
        ? feeUpdateLegacyFields.accountNumbers
        : this.state.requestType === "Existing"
          ? [this.state.accountNumber]
          : [],
      advisors: [
        { name: this.state.advisorName, isPrimary: true },
        ...this.state.additionalAdvisors.filter((n) => n).map((name) => ({ name, isPrimary: false })),
      ],
    };
    if (!payload.soloKQuestionnaire) delete payload.soloKQuestionnaire;
    return payload;
  };

  // --- Section 5: Submit ---
  handleSubmit = async () => {
    if (!this.isFormValid() || this.state.isSubmitting) {
      this._zeroFeeConfirmationInProgress = false;
      if (this.state.zeroFeeConfirmationPending) {
        this.setState({ zeroFeeConfirmationPending: false });
      }
      return;
    }

    const zeroFields = this.findZeroFees();
    if (zeroFields.length > 0 && !this.state.iarFeeWaiver) {
      this.setState({
        showZeroFeeModal: true,
        zeroFeeConfirmationPending: false,
      });
      return;
    }

    const now = new Date();
    const submissionDateTime = this.state.proposalDateTime || now.toUTCString();
    const localDateTime = now.toLocaleString();
    this.setState({ isSubmitting: true, submissionDateTime });

    const payload = this.buildSubmissionPayload();

    try {
      console.log("ISP Submission Payload:", JSON.stringify(payload, null, 2));

      // Get current user for context
      let userId = "";
      try {
        const currentUser = await getCurrentUser();
        userId = currentUser.username || currentUser.userId || "";
      } catch (e) {
        console.log("Could not get current user:", e);
      }

      const authHeaders = await getAuthHeaders();
      const apiName = "vc-proposal-generator";
      const path = "/proposal-generator/isp";

      // Send data flat in body — API Gateway mapping template wraps it
      // in "body-json" and adds "context" (user-id from Cognito) automatically.
      // Same pattern as proposalConfirm.jsx postData().
      await post({
        apiName,
        path,
        options: {
          headers: {
            ...authHeaders,
          },
          body: {
            ...payload,
            "date-time": submissionDateTime,
            "localDateTime": localDateTime,
            "user-name": userId,
            selectedPortfolio: this.state.selectedPortfolio,
            generateProposal: this.state.generateProposal || false,
            proposalName: this.state.proposalName || "",
            strategyDescription: this.getStrategyDescription(),
          },
        },
      }).response;

      this._zeroFeeConfirmationInProgress = false;
      this.setState({
        isSubmitting: false,
        zeroFeeConfirmationPending: false,
        showSuccess: true,
      });
    } catch (error) {
      console.error("ISP submission error:", error);
      // Still show success if the error is non-fatal (record may have been saved)
      // In production, check if the DynamoDB write succeeded before showing success
      this._zeroFeeConfirmationInProgress = false;
      this.setState({
        isSubmitting: false,
        zeroFeeConfirmationPending: false,
        submitError: error.message || "An error occurred while submitting. Please try again.",
      });
    }
  };

  handleCreateAnother = () => {
    this.setState({
      expandedSection: "1",
      advisorName: "",
      additionalAdvisors: [],
      strategy: "",
      bondType: "Taxable",
      bondTypePreferenceProvided: false,
      pathBondType: "",
      modelSelection: "",
      requestType: "",
      existingRequestAction: "",
      accountNumber: "",
      accountNumbers: [""],
      feeUpdateAccounts: [createEmptyFeeUpdateAccount()],
      clientProfileImaSubmitted: "",
      householding: "",
      householdRefName: "",
      householdRefLast4: "",
      householdRef: "",
      householdReason: "",
      householdReasonType: "",
      householdReasonOther: "",
      registrations: [
        { registration: "registration0", value: "", percentage: "100" },
      ],
      initialInvestmentAmount: "",
      accountNetWorthPct: "",
      accountIncomeNeed: "",
      accountIncomeAmount: "",
      accountIncomeStartDate: "",
      accountIncomeEndDate: "",
      accountIncomeFrequency: "",
      accountGoal: "",
      accountGoalOther: "",
      sourceOfFunds: [],
      sourceOfFundsText: {},
      soloKQuestionnaire: getEmptySoloKQuestionnaire(),
      minorName: "",
      relationshipToCustodian: "",
      custodialAdultName: "",
      custodianFullName: "",
      relationshipToPrimary: "",
      trustName: "",
      businessName: "",
      businessEinLast4: "",
      decedentName: "",
      decedentSsnLast4: "",
      relationshipToExecutor: "",
      fusionRegistrationType: "Single",
      pcsRegistrationType: "Single",
      standardRegistrationType: "Single",
      fusionRebalanceType: "Automatic",
      jointClients: [],
      deferredJointClients: [],
      showClientDialog: false,
      dialogEditIndex: -1,
      dialogCopySource: -1,
      capacityAnswers: {},
      willingnessAnswers: {},
      capacityTotal: 0,
      willingnessTotal: 0,
      combinedScore: 0,
      riskCategory: "",
      selectedPortfolio: "",
      portfolioOverride: false,
      overrideReason: "",
      overrideOtherText: "",
      managementFees: null,
      managementFeesLoading: false,
      customFees: null,
      customFeesLoading: false,
      customFeesError: "",
      feeUpdateStrategyFeeBlocks: [],
      feeUpdateStrategyFeesLoading: false,
      feeUpdateStrategyFeesError: "",
      iarFeeTier: "",
      selectedManagementFeeTierKey: "",
      availableManagementFeeTiers: [],
      availableManagementFeeTierOptions: [],
      iarFeeType: "",
      iarFeeFlat: "",
      iarFeeTiered: [],
      pathIarFeeType: "Fixed Percentage",
      pathIarFeeFlat: "",
      pathIarFeeTiered: [],
      iarFeeScheduleType: "",
      iarFeeWaiver: null,
      fusionAllocations: [],
      fusionSubFees: {},
      fusionSubFeesLoading: false,
      fusionFeeValidation: { isValid: true, issues: [] },
      feesSectionVisited: false,
      showZeroFeeModal: false,
      zeroFeeConfirmationPending: false,
      isSubmitting: false,
      showSuccess: false,
    });
  };

  // --- Validation ---
  getFeeUpdateAccountReferenceIssue = () =>
    getFeeUpdateAccountReferenceIssue(this.state.feeUpdateAccounts, {
      registrationTypes: getIspRegistrationTypes(
        this.props.lists?.registrationTypes || [],
        this.state.custodian,
        {
          requestType: this.state.requestType,
          existingRequestAction: this.state.existingRequestAction,
        },
      ),
      proposalTypes: this.props.lists?.proposalTypes || [],
      advisorNames: [
        this.state.advisorName,
        ...(this.state.additionalAdvisors || []),
      ],
    });

  isSection1Valid = () => {
    const {
      advisorName,
      clientProfileImaSubmitted,
      householding,
      requestType,
      accountNumber,
      householdRefName,
      householdRefLast4,
      householdReasonType,
      householdReasonOther,
      jointClients,
    } = this.state;
    if (!advisorName) return false;
    if (!requestType) return false;
    if (this.shouldAskClientProfileIma() && !clientProfileImaSubmitted) return false;
    if (requestType === "Existing" && !this.state.existingRequestAction) return false;
    // T19 — when householding is disallowed for the selected strategy, skip
    // the household-required checks entirely. Deferred-with-note strategies
    // still collect Yes/NA for the post-graduation householding section.
    if (this.shouldAskHouseholding()) {
      if (!householding) return false;
      if (householding === "Yes") {
        if (!householdRefName) return false;
        if (!householdRefLast4 || householdRefLast4.length < 4) return false;
      }
      if (householding === "No") {
        if (!householdReasonType) return false;
        if (householdReasonType === "Other" && !householdReasonOther) return false;
      }
    }
    if (requestType === "Existing") {
      if (this.isExistingFeeUpdate()) {
        if (this.hasInvalidPcsFeeUpdateRegistrationCount()) return false;
        const isStandardMultiRegistration =
          this.isStandardMultiRegistration();
        if (!areFeeUpdateAccountsComplete(this.state.feeUpdateAccounts, {
          requireAccountOwner:
            this.isPcsMultiRegistration() || isStandardMultiRegistration,
          skipRegistrationDetails: isStandardMultiRegistration,
        })) {
          return false;
        }
        if (getFeeUpdateStrategyCompatibilityIssue(this.state.feeUpdateAccounts)) {
          return false;
        }
        if (this.getFeeUpdateAccountReferenceIssue()) return false;
      } else if (!/^\d{4}$/.test(accountNumber || "")) {
        return false;
      }
    }
    if (jointClients.length === 0) return false;
    // Exactly one client must be flagged primary — we no longer auto-assign it.
    const primary = jointClients.find((c) => c.isPrimary);
    if (!primary) return false;
    // T10 — primary client must include last-4 of SSN
    if (!primary.ssnLast4 || primary.ssnLast4.length < 4) return false;
    if (
      !this.allowsSecondAccountHolderForState() &&
      jointClients.length > 1
    ) {
      return false;
    }
    if (this.isJointAccountHolderRequirementUnmet()) {
      return false;
    }
    return true;
  };

  // Uses the same ISP threshold/limit resolver as Section 2 so the displayed
  // helper, blur warning, form gate, and submitted PDF metadata cannot drift.
  getStrategyInvestmentLimits = () => {
    const { strategy } = this.state;
    if (!strategy) return null;
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === strategy);
    return resolveStrategyInvestmentLimits(strategy, pt);
  };

  // Validate one Fusion sub-strategy allocation's dollar amount against that
  // sub-strategy's investment limits (fusionLimits, falling back to limits).
  // The amount is the Section 2 "Initial Amount of Investment" scaled by the
  // allocation percentage. Returns an error string, or null when the
  // allocation is within range, has no amount yet, or the sub-strategy has no
  // configured limits.
  getFusionAllocationLimitError = (allocation) => {
    const strategy = String(allocation?.strategy || "").trim();
    const percentage = parseFloat(allocation?.percentage);
    if (!strategy || !Number.isFinite(percentage) || percentage <= 0) return null;

    const investment = parseFloat(
      String(this.state.initialInvestmentAmount).replace(/[^0-9.-]+/g, "")
    );
    if (!Number.isFinite(investment) || investment <= 0) return null;

    const amount = Math.round((investment * percentage) / 100);
    if (amount <= 0) return null;

    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === strategy);
    const limitsToUse = pt?.fusionLimits || pt?.limits;
    if (!limitsToUse) return null;

    const min = Number(limitsToUse.min);
    const max = Number(limitsToUse.max);
    const fmt = (value) =>
      value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
      });

    if (!isNaN(min) && amount < min) {
      return `Allocated ${fmt(amount)} is below the ${fmt(min)} minimum for this sub-strategy.`;
    }
    if (!isNaN(max) && amount > max) {
      return `Allocated ${fmt(amount)} exceeds the ${fmt(max)} maximum for this sub-strategy.`;
    }
    return null;
  };

  getModelSelectionConfig = () => {
    const { strategy } = this.state;
    if (!strategy) return null;
    if (this.isManagedMoneyMarketStrategy(strategy)) {
      return MMMS_SELECTION_CONFIG;
    }
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === strategy);
    return pt?.modelSelection || null;
  };

  getHouseholdReasonText = () => {
    if (this.state.householdReasonType === "Only account") {
      return "This is the only account in the household.";
    }
    if (this.state.householdReasonType === "Other") {
      return this.state.householdReasonOther || "";
    }
    return this.state.householdReason || "";
  };

  isExistingFeeUpdate = () =>
    this.state.requestType === "Existing" &&
    this.state.existingRequestAction === "Fee Update";

  isExistingRiskToleranceUpdate = () =>
    this.state.requestType === "Existing" &&
    this.state.existingRequestAction === "Risk Tolerance Update";

  isBondTypePromptShown = () =>
    !this.isManagedMoneyMarketStrategy() &&
    (this.isExistingRiskToleranceUpdate()
      ? this.hasRiskToleranceUpdateBondPreference()
      : this.hasBondOptions()) &&
    (!this.isReducedAccountInfoUpdate() || this.isExistingRiskToleranceUpdate());

  isBondTypePreferenceProvided = () =>
    this.isExistingRiskToleranceUpdate() &&
    this.isBondTypePromptShown() &&
    this.state.bondTypePreferenceProvided === true &&
    ["Taxable", "Municipal"].includes(this.state.bondType);

  isExistingModelChange = () =>
    this.state.requestType === "Existing" &&
    this.state.existingRequestAction === "Model Change";

  shouldAskClientProfileIma = () =>
    this.state.requestType === "New" ||
    (this.state.requestType === "Existing" &&
      isStrategyChangeWorkflowAction(this.state.existingRequestAction));

  shouldAskHouseholding = () =>
    this.state.requestType === "New" &&
    this.getStrategyHouseholdingMode() !== "disallowed";

  getAccountGoalSelections = () => {
    if (Array.isArray(this.state.accountGoal)) return this.state.accountGoal;
    return this.state.accountGoal ? [this.state.accountGoal] : [];
  };

  isOtherAccountGoalSelected = () =>
    this.isManagedMoneyMarketStrategy()
      ? this.getAccountGoalSelections().includes("Other")
      : this.state.accountGoal === "Funding for another goal";

  getAccountGoalForPayload = () => {
    if (this.isManagedMoneyMarketStrategy()) {
      return this.getAccountGoalSelections().join(", ");
    }
    return this.state.accountGoal || "";
  };

  getAccountGoalOtherForPayload = () =>
    this.isOtherAccountGoalSelected() ? this.state.accountGoalOther : "";

  getSelectedSourceOfFundsText = (
    sourceOfFunds = this.state.sourceOfFunds,
    sourceOfFundsText = this.state.sourceOfFundsText,
  ) => {
    const selected = Array.isArray(sourceOfFunds) ? sourceOfFunds : [];
    const text = sourceOfFundsText || {};
    const selectedText = {};
    if (selected.includes("Gift From Immediate Relative") && text["Gift From Immediate Relative"]) {
      selectedText["Gift From Immediate Relative"] = text["Gift From Immediate Relative"];
    }
    if (selected.includes("Other") && text.Other) {
      selectedText.Other = text.Other;
    }
    const advisorSoldSelected =
      selected.includes("Insurance/Annuity Contract") ||
      selected.includes("Mutual Funds/ETFs/UITs");
    if (advisorSoldSelected && text.advisorSoldFunds) {
      selectedText.advisorSoldFunds = text.advisorSoldFunds;
    }
    return selectedText;
  };

  getAccountGoalDisplay = () => {
    if (this.isManagedMoneyMarketStrategy()) {
      const parts = this.getAccountGoalSelections().map((goal) =>
        goal === "Other" && this.state.accountGoalOther
          ? `Other: ${this.state.accountGoalOther}`
          : goal,
      );
      return parts.join(", ");
    }
    return this.state.accountGoal === "Funding for another goal"
      ? this.state.accountGoalOther || "Funding for another goal"
      : this.state.accountGoal;
  };

  isReducedAccountInfoUpdate = () =>
    this.state.requestType === "Existing" &&
    this.isReducedExistingAccountAction(this.state.existingRequestAction);

  isFusionMultiRegistration = () =>
    this.isFusionStrategy() && this.state.fusionRegistrationType === "Multi";

  getEffectiveFusionAllocations = () => {
    const proposalTypes = this.props.lists?.proposalTypes || [];
    const rawAllocations = Array.isArray(this.state.fusionAllocations)
      ? this.state.fusionAllocations
      : [];
    const allocations = rawAllocations.map((allocation) =>
      isPrivateClientStrategyAlias(
        allocation?.strategy,
        proposalTypes,
      )
        ? this.normalizeFusionAllocations(
            [allocation],
            this.state.fusionRegistrationType,
          )[0]
        : { ...allocation },
    );
    if (this.isFusionMultiRegistration()) return allocations;
    return allocations.map((allocation) => {
      const effective = { ...allocation };
      if (Object.prototype.hasOwnProperty.call(effective, "registration")) {
        effective.registration = "";
      }
      if (Object.prototype.hasOwnProperty.call(effective, "accountOwner")) {
        effective.accountOwner = "";
      }
      return effective;
    });
  };

  isFusionAllocationSelectionValid = () => {
    if (this.isExistingFeeUpdate()) return true;
    if (!this.isFusionStrategy()) return true;
    const fusionAllocations = this.getEffectiveFusionAllocations();
    const registrationTypes = getIspRegistrationTypes(
      this.props.lists?.registrationTypes || [],
      this.state.custodian,
      {
        requestType: this.state.requestType,
        existingRequestAction: this.state.existingRequestAction,
      },
    );
    if (fusionAllocations.length === 0) return false;
    if (
      fusionAllocations.some(
        (allocation) =>
          !allocation.strategy ||
          !allocation.riskTolerance ||
          (!isPrivateClientStrategyAlias(
            allocation.strategy,
            this.props.lists?.proposalTypes || [],
          ) &&
            !allocation.taxSensitivity) ||
          (this.isFusionMultiRegistration() &&
            (!allocation.registration ||
              !allocation.accountOwner ||
              !isIspRegistrationAvailable(
                allocation.registration,
                registrationTypes,
              ))) ||
          !allocation.percentage ||
          parseFloat(allocation.percentage) <= 0,
      )
    ) {
      return false;
    }
    const total = fusionAllocations.reduce(
      (sum, allocation) =>
        sum + (parseFloat(allocation.percentage) || 0),
      0,
    );
    if (Math.abs(total - 100) > 0.01) return false;
    return !fusionAllocations.some((allocation) =>
      this.getFusionAllocationLimitError(allocation),
    );
  };

  hasJointRegistrationRequirement = (
    registrations = this.isExistingFeeUpdate()
      ? (this.state.feeUpdateAccounts || []).map((account) => ({
          value: account?.registration,
        }))
      : this.state.registrations,
  ) =>
    (registrations || []).some((registration) => {
      const value = String(registration?.value || "").trim().toLowerCase();
      return value === "joint" || value === "joint tod";
    });

  allowsSecondAccountHolderForState = (state = this.state) => {
    const isFeeUpdate =
      state.requestType === "Existing" &&
      state.existingRequestAction === "Fee Update";

    if (isFeeUpdate) {
      if (
        this.isPcsMultiRegistration(state) ||
        this.isStandardMultiRegistration(state)
      ) return true;
      return (state.feeUpdateAccounts || []).some((account) => {
        const registration = String(account?.registration || "").trim();
        return (
          registration &&
          getRegistrationRules(registration, state.custodian)
            .allowsSecondAccountHolder
        );
      });
    }

    if (
      this.isPcsMultiRegistration(state) ||
      this.isStandardMultiRegistration(state)
    ) return true;

    return getRegistrationRules(
      state.registrations?.[0]?.value,
      state.custodian,
    ).allowsSecondAccountHolder;
  };

  isJointAccountHolderRequirementUnmet = () =>
    !this.isFusionMultiRegistration() &&
    this.hasJointRegistrationRequirement() &&
    this.state.jointClients.length < 2;

  getFusionRebalanceType = () =>
    this.isFusionMultiRegistration()
      ? "Manual"
      : this.state.fusionRebalanceType || "Automatic";

  shouldSkipRiskTolerance = () =>
    this.isExistingFeeUpdate() || this.isManagedMoneyMarketStrategy();

  shouldSkipFees = () =>
    !getRequestCapabilities(
      this.state.requestType,
      this.state.existingRequestAction,
    ).asksFees;

  canUseDollarFlatFee = () =>
    /wealthkeel|chad chubb/i.test(String(this.state.advisorName || ""));

  shouldSkipRiskQuestionnaireAnswers = () =>
    this.shouldSkipRiskTolerance() || this.isExistingModelChange();

  getRegistrationDisplay = () => {
    if (
      this.isFusionMultiRegistration() ||
      this.isPcsMultiRegistration() ||
      this.isStandardMultiRegistration()
    ) {
      return "";
    }
    return this.state.registrations?.[0]?.value || "";
  };

  getAllowedJointClients = () => {
    const clients = this.getOrderedJointClients();
    if (this.allowsSecondAccountHolderForState() || clients.length <= 1) {
      return clients;
    }
    const primary = clients.find((client) => client?.isPrimary) || clients[0];
    return primary ? [{ ...primary, isPrimary: true }] : [];
  };

  hasSoloKRegistration = (registrations = this.state.registrations) => {
    if (
      (this.isFusionMultiRegistration() ||
        this.isPcsMultiRegistration() ||
        this.isStandardMultiRegistration()) &&
      registrations === this.state.registrations
    ) {
      return false;
    }
    return (registrations || []).some((registration) =>
      isSoloKRegistrationValue(registration?.value)
    );
  };

  isSoloKQuestionnaireValid = () => {
    const q = {
      ...getEmptySoloKQuestionnaire(),
      ...this.state.soloKQuestionnaire,
    };
    if (!q.sponsoringBusinessName.trim()) return false;
    if (!q.ownerCount || Number(q.ownerCount) < 1) return false;
    if (q.employeeCount === "" || Number(q.employeeCount) < 0) return false;
    if (exceedsSoloKParticipantLimit(q)) return false;
    if (
      shouldAskSoloKSpouseQuestion(q) &&
      !["Yes", "No"].includes(q.secondParticipantIsSpouse)
    ) return false;
    if (!q.anticipatesEmployees) return false;
    if (!q.planType) return false;
    if (q.planType === "Existing Plan" && !q.existingPlanDocumentsProvided) return false;
    if (!Array.isArray(q.ownershipEvidence) || q.ownershipEvidence.length === 0) return false;
    if (!q.certificationNoEmployees) return false;
    if (!q.certificationContributionLimits) return false;
    return true;
  };

  // T19 — read the per-strategy householding mode from proposal-types.
  // Possible values:
  //   - "allowed" (default): current behavior; Yes/No prompt + conditional fields.
  //   - "disallowed": hide the householding section entirely (e.g., Managed
  //     Money Market Solutions). The PDF also omits household placeholders.
  //   - "deferred-with-note": show the Yes/NA prompt with a static note
  //     (e.g., Elements: "Householding will not apply until account graduates
  //     to selected path."). PDF labels the result after account graduation.
  getStrategyHouseholdingMode = () => {
    if (
      !getRegistrationRules(
        this.state.registrations?.[0]?.value,
        this.state.custodian,
      ).allowsHouseholding
    ) {
      return "disallowed";
    }
    const strategy = this.state.strategy;
    if (!strategy) return "allowed";
    const pt = this.props.lists?.proposalTypes?.find((p) => p.name === strategy);
    const mode = pt?.householdingMode;
    if (mode === "disallowed" || mode === "deferred-with-note") return mode;
    return "allowed";
  };

  // T15 — true when the primary registration is UGMA or UTMA. Used by both
  // the form (to surface conditional minor-name + relationship inputs) and
  // by validation/payload to require/serialize them.
  isUgmaUtmaRegistration = () => {
    if (
      this.isFusionMultiRegistration() ||
      this.isPcsMultiRegistration() ||
      this.isStandardMultiRegistration()
    ) {
      return false;
    }
    const value = this.state.registrations?.[0]?.value || "";
    if (!value) return false;
    const upper = value.toUpperCase();
    return upper.includes("UGMA") || upper.includes("UTMA");
  };

  isBusinessRegistration = () => {
    if (
      this.isFusionMultiRegistration() ||
      this.isPcsMultiRegistration() ||
      this.isStandardMultiRegistration()
    ) {
      return false;
    }
    const value = this.state.registrations?.[0]?.value || "";
    if (!value) return false;
    const regType = getIspRegistrationTypes(
      this.props.lists?.registrationTypes || [],
      this.state.custodian,
      {
        requestType: this.state.requestType,
        existingRequestAction: this.state.existingRequestAction,
      },
    ).find((registration) => registration.name === value);
    return regType?.category === "Business Account";
  };

  isEstateRegistration = () => {
    if (
      this.isFusionMultiRegistration() ||
      this.isPcsMultiRegistration() ||
      this.isStandardMultiRegistration()
    ) {
      return false;
    }
    return isEstateRegistrationValue(this.state.registrations?.[0]?.value);
  };

  isSection2Valid = () => {
    const {
      strategy,
      registrations,
      sourceOfFunds,
      initialInvestmentAmount,
      accountNetWorthPct,
      accountIncomeNeed,
      accountIncomeAmount,
      accountIncomeStartDate,
      accountIncomeFrequency,
      accountGoal,
      accountGoalOther,
    } = this.state;
    if (this.isExistingFeeUpdate()) {
      const isStandardMultiRegistration =
        this.isStandardMultiRegistration();
      const baseValid =
        !this.hasInvalidPcsFeeUpdateRegistrationCount() &&
        areFeeUpdateAccountsComplete(this.state.feeUpdateAccounts, {
          requireAccountOwner:
            this.isPcsMultiRegistration() || isStandardMultiRegistration,
          skipRegistrationDetails: isStandardMultiRegistration,
        }) &&
        !getFeeUpdateStrategyCompatibilityIssue(this.state.feeUpdateAccounts) &&
        !this.getFeeUpdateAccountReferenceIssue();
      if (!baseValid || !isStandardMultiRegistration) return baseValid;
      return areStandardRegistrationRowsComplete(
        this.state.feeUpdateAccounts,
        {
          strategy: this.state.strategy,
          proposalTypes: this.props.lists?.proposalTypes || [],
          registrationTypes: this.props.lists?.registrationTypes || [],
          pathStrategy: this.getPathStrategyName(),
          investmentLimits: this.getStrategyInvestmentLimits(),
          existingRequestAction: this.state.existingRequestAction,
        },
      );
    }
    if (!strategy) return false;
    if (
      !isStrategyEligibleForRequest(
        strategy,
        this.state.requestType,
        this.state.existingRequestAction,
        getProgramDisplayName(
          strategy,
          this.props.lists?.proposalTypes || [],
        ),
      )
    ) {
      return false;
    }
    const usesRowRegistrations =
      this.isFusionMultiRegistration() ||
      this.isPcsMultiRegistration() ||
      this.isStandardMultiRegistration();
    if (this.isPcsMultiRegistration()) {
      const registrationTypes = getIspRegistrationTypes(
        this.props.lists?.registrationTypes || [],
        this.state.custodian,
        {
          requestType: this.state.requestType,
          existingRequestAction: this.state.existingRequestAction,
        },
      );
      if (!arePcsRegistrationRowsComplete(registrations)) return false;
      if (
        registrations.some(
          (registration) =>
            !isIspRegistrationAvailable(registration.value, registrationTypes),
        )
      ) {
        return false;
      }
    }
    if (this.isStandardMultiRegistration()) {
      const registrationTypes = getIspRegistrationTypes(
        this.props.lists?.registrationTypes || [],
        this.state.custodian,
        {
          requestType: this.state.requestType,
          existingRequestAction: this.state.existingRequestAction,
        },
      );
      if (
        registrations.some(
          (registration) =>
            !isIspRegistrationAvailable(registration.value, registrationTypes),
        ) ||
        !areStandardRegistrationRowsComplete(registrations, {
          strategy,
          proposalTypes: this.props.lists?.proposalTypes || [],
          registrationTypes,
          pathStrategy: this.getPathStrategyName(),
          investmentLimits: this.getStrategyInvestmentLimits(),
          existingRequestAction: this.state.existingRequestAction,
        })
      ) {
        return false;
      }
    }
    if (!usesRowRegistrations) {
      const primaryRegistration = registrations[0]?.value;
      if (!primaryRegistration) return false;
      if (
        !isIspRegistrationAvailable(
          primaryRegistration,
          getIspRegistrationTypes(
            this.props.lists?.registrationTypes || [],
            this.state.custodian,
            {
              requestType: this.state.requestType,
              existingRequestAction: this.state.existingRequestAction,
            },
          ),
        )
      ) {
        return false;
      }
    }
    if (!this.isFusionAllocationSelectionValid()) return false;
    if (!this.getFusionAllocationRiskValidation().isValid) return false;
    if (this.isReducedAccountInfoUpdate()) {
      if (usesRowRegistrations) return true;
      return !getRegistrationDetailValidationIssue(
        this.state,
        registrations[0]?.value,
        this.props.lists?.registrationTypes || [],
        { required: true },
      );
    }
    const modelConfig = this.getModelSelectionConfig();
    if (modelConfig && !this.state.modelSelection) return false;
    // T15 — UGMA/UTMA registrations require minor name + relationship.
    if (!usesRowRegistrations) {
      const registrationRules = getRegistrationRules(
        registrations[0]?.value,
        this.state.custodian,
        {
          requestType: this.state.requestType,
          existingRequestAction: this.state.existingRequestAction,
        },
      );
      if (!registrationRules.isAvailableForCustodian) return false;
      if (
        getRegistrationDetailValidationIssue(
          this.state,
          registrations[0]?.value,
          this.props.lists?.registrationTypes || [],
          { required: true },
        )
      ) {
        return false;
      }
    }
    // Custodian is required when the custodians list is populated (matches the
    // UI green/grey indicator on the custodian dropdown).
    const custodians = this.props.lists?.custodians;
    if (Array.isArray(custodians) && custodians.length > 0 && !this.state.custodian) return false;
    // Bond type required when the strategy + registration combo offers a choice.
    if (
      !this.isStandardMultiRegistration() &&
      this.hasBondOptions() &&
      !this.state.bondType
    ) return false;
    if (sourceOfFunds.length === 0) return false;
    if (sourceOfFunds.includes("Gift From Immediate Relative") && !this.state.sourceOfFundsText["Gift From Immediate Relative"]?.trim()) return false;
    if (sourceOfFunds.includes("Other") && !this.state.sourceOfFundsText["Other"]?.trim()) return false;
    // The asterisked sources (Insurance/Annuity, Mutual Funds/ETFs/UITs) share
    // a single advisorSoldFunds disclosure text — required if either is picked.
    const needsAdvisorSold =
      sourceOfFunds.includes("Insurance/Annuity Contract") ||
      sourceOfFunds.includes("Mutual Funds/ETFs/UITs");
    if (needsAdvisorSold && !this.state.sourceOfFundsText["advisorSoldFunds"]?.trim()) return false;
    if (this.isStandardMultiRegistration()) return true;
    if (!initialInvestmentAmount) return false;
    // Enforce strategy min/max on the investment amount — the UI shows a red
    // border when out of range, but until now nothing blocked submission.
    const investmentNum =
      parseFloat(String(initialInvestmentAmount).replace(/[^0-9.-]+/g, "")) || 0;
    const limits = this.getStrategyInvestmentLimits();
    if (!isInvestmentAmountWithinLimits(investmentNum, limits)) return false;
    if (!/^\d+$/.test(String(accountNetWorthPct || ""))) return false;
    if (Number(accountNetWorthPct) < 0 || Number(accountNetWorthPct) > 100) return false;
    if (accountIncomeNeed === "Yes") {
      if (!accountIncomeAmount) return false;
      const incomeNum = parseFloat(String(accountIncomeAmount).replace(/[^0-9.-]+/g, "")) || 0;
      const investmentNum = parseFloat(String(initialInvestmentAmount).replace(/[^0-9.-]+/g, "")) || 0;
      if (investmentNum > 0 && incomeNum > investmentNum) return false;
      if (!accountIncomeStartDate) return false;
      const todayIso = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .split("T")[0];
      if (accountIncomeStartDate < todayIso) return false;
      if (this.state.accountIncomeEndDate && this.state.accountIncomeEndDate <= accountIncomeStartDate) return false;
      if (!accountIncomeFrequency) return false;
    }
    if (this.isManagedMoneyMarketStrategy()) {
      const goals = this.getAccountGoalSelections();
      if (goals.length === 0) return false;
      if (goals.includes("Other") && !accountGoalOther) return false;
    } else {
      if (!accountGoal) return false;
      if (accountGoal === "Funding for another goal" && !accountGoalOther) return false;
    }
    return true;
  };

  isSection3Valid = () => {
    if (this.shouldSkipRiskTolerance()) return true;
    if (this.isExistingModelChange()) return !!this.state.selectedPortfolio;
    const {
      capacityAnswers, willingnessAnswers, selectedPortfolio,
      portfolioOverride, overrideReason, overrideOtherText,
    } = this.state;
    const capAnswered = CAPACITY_QUESTIONS.filter((q) => capacityAnswers[q.id] !== undefined).length;
    const willAnswered = WILLINGNESS_QUESTIONS.filter((q) => willingnessAnswers[q.id] !== undefined).length;
    if (capAnswered < CAPACITY_QUESTIONS.length) return false;
    if (willAnswered < WILLINGNESS_QUESTIONS.length) return false;
    if (!selectedPortfolio) return false;
    if (this.isManagedMoneyMarketStrategy()) {
      const canonical = canonicalizeMmmsFundSelection(
        this.state.modelSelection,
        this.state.bondType,
      );
      return !!canonical && selectedPortfolio === canonical;
    }
    if (
      !portfolioOverride &&
      !portfolioMatchesRiskCategory(
        selectedPortfolio,
        this.state.riskCategory,
        this.getRiskToleranceAliases(),
      )
    ) {
      return false;
    }
    if (portfolioOverride) {
      if (!overrideReason) return false;
      if (overrideReason === "Other" && !overrideOtherText) return false;
    }
    return true;
  };

  isSection4Valid = () => {
    if (this.shouldSkipFees()) return true;
    if (!this.state.feesSectionVisited) return false;
    const feeUpdateStrategies = this.getFeeUpdateStrategies();
    if (feeUpdateStrategies.length > 1) {
      if (
        this.state.feeUpdateStrategyFeesLoading ||
        this.state.feeUpdateStrategyFeesError ||
        this.state.feeUpdateStrategyFeeBlocks.length !==
          feeUpdateStrategies.length
      ) {
        return false;
      }
    }
    const iarFeeMax = this.getIarFeeMax();
    const isIarFeeComplete = (type, flat, tiered) => {
      const effectiveType = type || "Fixed Percentage";
      if (effectiveType === "Fixed Percentage") {
        if (flat === "" || flat === undefined) return false;
        if (!isValidIarPercentage(flat, { max: iarFeeMax })) return false;
        return true;
      }
      if (effectiveType === "Tiered") {
        const labels = this.getFeeScheduleLabels();
        if (labels.length === 0) return false;
        const tieredRates = normalizeIarFeeTiered(tiered, labels);
        const allFilled = tieredRates.every((t) => t.rate !== undefined && t.rate !== "");
        if (!allFilled) return false;
        if (
          tieredRates.some(
            (t) => !isValidIarPercentage(t.rate, { max: iarFeeMax }),
          )
        ) return false;
        return true;
      }
      if (effectiveType === "Flat Fee") {
        return /^\d+(?:\.\d{1,2})?$/.test(String(flat || ""));
      }
      return false;
    };

    if (this.isFusionStrategy()) {
      const { fusionAllocations, iarFeeTier, fusionFeeValidation } = this.state;
      if (!iarFeeTier) return false;
      // Lenient sub-strategy fee lookup: management-fees rows in DynamoDB may
      // be keyed by the IM tier alone ("Standard") while the form's selected
      // tier carries an IAR-schedule suffix ("Standard WK"). resolveFusionSubFees
      // tries the exact selected tier, the collapsed label, then any cached
      // entry for the strategy. Matches buildFusionFeeBlocks / fetchFusionSubFees.
      const exactTier = this.getSelectedManagementFeeTierKey();
      const collapsed = collapseTierLabel(exactTier);
      if (
        fusionAllocations.some(
          (allocation) =>
            !validateManagementFeeTable(
              this.resolveFusionSubFees(
                allocation.strategy,
                exactTier,
                collapsed,
              ),
            ).isComplete,
        )
      ) return false;
      if (!fusionFeeValidation?.isValid) return false;
    } else {
      if (!this.state.iarFeeTier) return false;
    }

    // Fee values must be present and within range. Explicit zero is valid here
    // so the final Submit ISP action can show the one required acknowledgment.
    if (this.hasPath()) {
      if (!isIarFeeComplete(
        this.getEffectiveIarFeeType(),
        this.state.iarFeeFlat,
        this.state.iarFeeTiered,
      )) return false;
    } else {
      const effectiveIarFeeType = this.getEffectiveIarFeeType();
      if (!effectiveIarFeeType) return false;
      if (!isIarFeeComplete(
        effectiveIarFeeType,
        this.state.iarFeeFlat,
        this.state.iarFeeTiered,
      )) return false;
    }
    if (this.hasPath()) {
      if (!this.state.pathIarFeeType) return false;
      if (!isIarFeeComplete(
        this.state.pathIarFeeType,
        this.state.pathIarFeeFlat,
        this.state.pathIarFeeTiered,
      )) return false;
    }
    return true;
  };

  isSection5Valid = () => {
    return (
      this.isSection1Valid() &&
      this.isSection2Valid() &&
      (this.shouldSkipRiskTolerance() || this.isSection3Valid()) &&
      (this.shouldSkipFees() || this.isSection4Valid()) &&
      this.getFusionAllocationRiskValidation().isValid
    );
  };

  isFormValid = () => {
    return this.isSection5Valid();
  };

  render() {
    const { expandedSection } = this.state;
    const feeUpdateStrategies = Array.from(
      new Set(
        (this.state.feeUpdateAccounts || [])
          .map((account) => String(account.strategy || "").trim())
          .filter(Boolean),
      ),
    );
    const isMixedStrategyFeeUpdate =
      this.isExistingFeeUpdate() && feeUpdateStrategies.length > 1;
    const ispLists = {
      ...this.props.lists,
      registrationTypes: getIspRegistrationTypes(
        this.props.lists?.registrationTypes || [],
        this.state.custodian,
        {
          requestType: this.state.requestType,
          existingRequestAction: this.state.existingRequestAction,
        },
      ),
    };

    // Build values objects for child components
    const householdingMode = this.getStrategyHouseholdingMode();
    const section1Values = {
      advisorName: this.state.advisorName,
      strategy: this.state.strategy,
      requestType: this.state.requestType,
      existingRequestAction: this.state.existingRequestAction,
      accountNumber: this.state.accountNumber,
      accountNumbers: this.state.accountNumbers,
      feeUpdateAccounts: this.state.feeUpdateAccounts,
      pcsRegistrationType: this.state.pcsRegistrationType || "Single",
      standardRegistrationType:
        this.state.standardRegistrationType || "Single",
      clientProfileImaSubmitted: this.state.clientProfileImaSubmitted,
      householding: this.state.householding,
      householdRefName: this.state.householdRefName,
      householdRefLast4: this.state.householdRefLast4,
      householdReason: this.state.householdReason,
      householdReasonType: this.state.householdReasonType,
      householdReasonOther: this.state.householdReasonOther,
      registrations: this.getSingleRegistrationState(),
      // T19 — branches the Householding UI by per-strategy DynamoDB flag.
      householdingMode,
    };

    const section2Values = {
      advisorName: this.state.advisorName,
      additionalAdvisors: this.state.additionalAdvisors,
      requestType: this.state.requestType,
      existingRequestAction: this.state.existingRequestAction,
      custodian: this.state.custodian,
      strategy: this.state.strategy,
      fusionRegistrationType: this.state.fusionRegistrationType || "Single",
      pcsRegistrationType: this.state.pcsRegistrationType || "Single",
      standardRegistrationType:
        this.state.standardRegistrationType || "Single",
      fusionRebalanceType: this.getFusionRebalanceType(),
      bondType: this.state.bondType,
      bondTypePreferenceProvided: this.state.bondTypePreferenceProvided,
      showPathBondType: this.hasPathBondChoice(),
      pathBondType: this.state.pathBondType,
      pathStrategyName: this.getPathStrategyName(),
      modelSelection: this.state.modelSelection,
      initialInvestmentAmount: this.state.initialInvestmentAmount,
      accountNetWorthPct: this.state.accountNetWorthPct,
      accountIncomeNeed: this.state.accountIncomeNeed,
      accountIncomeAmount: this.state.accountIncomeAmount,
      accountIncomeStartDate: this.state.accountIncomeStartDate,
      accountIncomeEndDate: this.state.accountIncomeEndDate,
      accountIncomeFrequency: this.state.accountIncomeFrequency,
      accountGoal: this.state.accountGoal,
      accountGoalOther: this.state.accountGoalOther,
      sourceOfFunds: this.state.sourceOfFunds,
      sourceOfFundsText: this.state.sourceOfFundsText,
      isSoloKRegistration: this.hasSoloKRegistration(),
      soloKQuestionnaire: this.state.soloKQuestionnaire,
      // T15 — surfaced/required only when registrations[0] is UGMA/UTMA.
      isUgmaUtma: this.isUgmaUtmaRegistration(),
      minorName: this.state.minorName,
      relationshipToCustodian: this.state.relationshipToCustodian,
      custodialAdultName:
        this.state.custodianFullName || this.state.custodialAdultName,
      custodianFullName:
        this.state.custodianFullName || this.state.custodialAdultName,
      relationshipToPrimary: this.state.relationshipToPrimary,
      trustName: this.state.trustName,
      isBusinessRegistration: this.isBusinessRegistration(),
      businessName: this.state.businessName,
      businessEinLast4: this.state.businessEinLast4,
      isEstateRegistration: this.isEstateRegistration(),
      decedentName: this.state.decedentName,
      decedentSsnLast4: this.state.decedentSsnLast4,
      relationshipToExecutor: this.state.relationshipToExecutor,
      // T19 — surfaced alongside the strategy picker so the user learns how
      // the selected strategy affects Householding (which lives back in
      // Section 1, before the strategy is chosen).
      householdingMode: this.getStrategyHouseholdingMode(),
    };

    return (
      <div>
        <h1 style={{ display: "inline-block" }}>
          {this.state.isEditing
            ? "Edit Investment Strategy Plan and Fee Disclosure (ISP)"
            : "Create Investment Strategy Plan and Fee Disclosure (ISP)"}
        </h1>
        <div
          className="d-flex flex-wrap justify-content-between align-items-start mb-3"
          style={{ gap: "10px" }}
        >
          <p
            id="ispPrefillResetDescription"
            className="text-muted mb-0"
            style={{ fontSize: "14px", maxWidth: "760px" }}
          >
            All fields are required unless marked <em>(optional)</em>.
            Fields turn <span style={{ color: "green", fontWeight: "bold" }}>green</span> when completed. Resetting prefill suggestions does not change anything already entered.
          </p>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            aria-describedby="ispPrefillResetDescription"
            onClick={this.handleResetPrefillSuggestions}
          >
            Reset Prefill Suggestions
          </button>
        </div>

        {this.state.prefillResetNotice && (
          <div className="alert alert-success py-2" role="status">
            Prefill suggestions were reset. Current ISP answers were not changed.
          </div>
        )}

        {this.state.isEditing && (
          <div className="alert alert-info" role="status">
            <strong>Editing an existing ISP.</strong>{" "}
            Submitting your changes will update this submission instead of creating a copy.
          </div>
        )}

        {this.state.proposalPrefillInfo && (
          <div className="alert alert-info alert-dismissible fade show" role="alert">
            <strong>Pre-filled from Proposal:</strong> {this.state.proposalPrefillInfo}
            <button
              type="button"
              className="close"
              aria-label="Close"
              onClick={() => this.setState({ proposalPrefillInfo: "" })}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        )}

        <CollapsibleSection
          sectionNumber="1"
          title="Client Information"
          isExpanded={expandedSection === "1"}
          isValid={this.isSection1Valid()}
          onToggle={() => this.toggleSection("1")}
        >
          <ISPClientProfile
            values={section1Values}
            onChange={this.handleFieldChange}
            lists={ispLists}
            onAccountNumberChange={this.handleAccountNumberChange}
            onFeeUpdateAccountsChange={this.handleFeeUpdateAccountsChange}
            showJointAccountHolderWarning={
              this.isJointAccountHolderRequirementUnmet()
            }
            jointClients={this.state.jointClients}
            showClientDialog={this.state.showClientDialog}
            dialogEditIndex={this.state.dialogEditIndex}
            dialogCopySource={this.state.dialogCopySource}
            onAddClient={this.showAddClientDialog}
            onEditClient={this.showEditClientDialog}
            onCloseDialog={this.hideClientDialog}
            onSaveClient={this.handleSaveClient}
            onCopyClient={this.copyClient}
            onRemoveClient={this.removeClient}
            clientNameHistory={this.getClientNameHistory()}
            prefillGeneration={this.state.prefillGeneration}
            allowsSecondAccountHolder={this.allowsSecondAccountHolderForState()}
            additionalAdvisors={this.state.additionalAdvisors}
            onAddAdvisor={() => this.setState((s) => ({ additionalAdvisors: [...s.additionalAdvisors, ""] }))}
            onChangeAdvisor={(index, value) => {
              this.setState((s) => {
                const updated = [...s.additionalAdvisors];
                updated[index] = value;
                return { additionalAdvisors: updated };
              });
            }}
            onRemoveAdvisor={(index) => {
              this.setState({
                confirmModal: {
                  title: "Remove IAR",
                  message: `Are you sure you want to remove this IAR?`,
                  onConfirm: () => this.setState((s) => ({
                    additionalAdvisors: s.additionalAdvisors.filter((_, i) => i !== index),
                    confirmModal: null,
                  })),
                },
              });
            }}
          />
        </CollapsibleSection>

        <CollapsibleSection
          sectionNumber="2"
          title="Account Information"
          isExpanded={expandedSection === "2"}
          isValid={this.isSection2Valid()}
          onToggle={() => this.toggleSection("2")}
        >
          <ISPAccountInfo
            values={section2Values}
            onChange={this.handleFieldChange}
            lists={ispLists}
            registrations={this.state.registrations}
            onRegistrationChange={this.handleRegistrationChange}
            onRegistrationOwnerChange={this.handleRegistrationOwnerChange}
            feeUpdateAccounts={this.state.feeUpdateAccounts}
            onStandardRegistrationFieldChange={
              this.handleStandardRegistrationFieldChange
            }
            onAddRegistration={this.addRegistration}
            onRemoveRegistration={this.removeRegistration}
            onRemoveStandardRegistration={
              this.removeStandardRegistration
            }
            onSoloKChange={this.handleSoloKQuestionnaireChange}
            onSoloKOwnershipEvidenceToggle={this.handleSoloKOwnershipEvidenceToggle}
            onSourceOfFundsToggle={this.handleSourceOfFundsToggle}
            onSourceOfFundsTextChange={this.handleSourceOfFundsTextChange}
            showBondType={this.isBondTypePromptShown()}
            availableBondTypes={this.getAvailableBondTypesForModel()}
            modelSelectionConfig={this.getModelSelectionConfig()}
            showJointAccountHolderWarning={this.isJointAccountHolderRequirementUnmet()}
            fusionAllocations={this.state.fusionAllocations}
            onFusionAllocationsChange={this.handleFusionAllocationsChange}
            jointClients={this.state.jointClients}
            selectedManagementFeeTierKey={this.state.selectedManagementFeeTierKey}
            fusionSubFees={this.state.fusionSubFees}
            fusionAllocationLimitErrors={this.state.fusionAllocations.map((allocation) =>
              this.getFusionAllocationLimitError(allocation)
            )}
            fusionAllocationRiskValidation={this.getFusionAllocationRiskValidation()}
            prefillGeneration={this.state.prefillGeneration}
            fundingAmountHistory={this.getFundingAmountHistory()}
          />
        </CollapsibleSection>

        <CollapsibleSection
          sectionNumber="3"
          title="Risk Tolerance Questionnaire"
          isExpanded={expandedSection === "3"}
          isValid={this.isSection3Valid()}
          onToggle={() => this.toggleSection("3")}
        >
          {this.shouldSkipRiskTolerance() ? (
            <div className="alert alert-info mb-0">
              {this.isManagedMoneyMarketStrategy()
                ? "Risk Tolerance Questionnaire is not required for Managed Money Market Solution."
                : "Risk Tolerance Questionnaire is not required for this request."}
            </div>
          ) : this.isExistingModelChange() ? (
            this.renderAllocationOnlyRiskSelection()
          ) : (
            <RiskQuestionnaire
              capacityAnswers={this.state.capacityAnswers}
              willingnessAnswers={this.state.willingnessAnswers}
              capacityTotal={this.state.capacityTotal}
              willingnessTotal={this.state.willingnessTotal}
              combinedScore={this.state.combinedScore}
              riskCategory={this.state.riskCategory}
              selectedPortfolio={this.state.selectedPortfolio}
              portfolioOverride={this.state.portfolioOverride}
              overrideReason={this.state.overrideReason}
              overrideOtherText={this.state.overrideOtherText}
              riskToleranceOptions={this.getRiskToleranceOptions()}
              riskToleranceAliases={this.getRiskToleranceAliases()}
              strategySelected={!!this.state.strategy}
              strategy={this.state.strategy}
              lockPortfolioSelection={this.isManagedMoneyMarketStrategy()}
              onAnswerChange={this.handleRiskAnswerChange}
              onPortfolioChange={this.handlePortfolioChange}
              onOverrideToggle={this.handleOverrideToggle}
              onOverrideReasonChange={this.handleOverrideReasonChange}
              onOverrideOtherTextChange={this.handleOverrideOtherTextChange}
              onReset={this.handleRiskReset}
              riskChartUrl={this.state.riskChartUrl}
            />
          )}
        </CollapsibleSection>

        <CollapsibleSection
          sectionNumber="4"
          title="Fees"
          isExpanded={expandedSection === "4"}
          isValid={this.isSection4Valid()}
          onToggle={() => this.toggleSection("4")}
        >
          {this.shouldSkipFees() ? (
            <div className="alert alert-info mb-0">
              Fees are not changed by this request.
            </div>
          ) : <>{isMixedStrategyFeeUpdate && (
            <div className="alert alert-info py-2" role="status">
              The IAR fee instruction entered below applies to every listed
              account. The PDF will show the Investment Management Fee schedule
              for each selected strategy separately.
            </div>
          )}
          {isMixedStrategyFeeUpdate &&
            this.state.feeUpdateStrategyFeesLoading && (
              <div className="alert alert-info py-2" role="status">
                Loading each selected strategy's management fee schedule...
              </div>
            )}
          {isMixedStrategyFeeUpdate &&
            this.state.feeUpdateStrategyFeesError && (
              <div className="alert alert-danger py-2" role="alert">
                {this.state.feeUpdateStrategyFeesError}
              </div>
            )}
          <ISPFeeSchedule
            strategy={this.state.strategy}
            pathStrategy={this.getPathStrategyName()}
            lists={ispLists}
            customFees={this.state.customFees}
            customFeesLoading={this.state.customFeesLoading}
            customFeesError={this.state.customFeesError}
            onRetryFeeSchedules={this.retryFeeSchedules}
            managementFees={this.state.managementFees}
            managementFeesLoading={this.state.managementFeesLoading}
            managementFeeTierOptions={this.state.availableManagementFeeTierOptions}
            hideInvestmentManagementFees={isMixedStrategyFeeUpdate}
            isFusion={this.isFusionStrategy()}
            fusionAllocations={this.state.fusionAllocations}
            fusionFeeBlocks={this.buildFusionFeeBlocks()}
            fusionSubFees={this.state.fusionSubFees}
            fusionSubFeesLoading={this.state.fusionSubFeesLoading}
            fusionFeeValidation={this.state.fusionFeeValidation}
            hasPath={this.hasPath()}
            hasInitialFeeStage={this.hasInitialFeeStage()}
            allowFlatFee={this.canUseDollarFlatFee()}
            values={{
              iarFeeTier: this.state.iarFeeTier,
              selectedManagementFeeTierKey: this.state.selectedManagementFeeTierKey,
              iarFeeType: this.state.iarFeeType,
              iarFeeFlat: this.state.iarFeeFlat,
              iarFeeTiered: this.state.iarFeeTiered,
              iarFeeScheduleType: this.state.iarFeeScheduleType,
              iarFeeWaiver: this.state.iarFeeWaiver,
              pathIarFeeType: this.state.pathIarFeeType,
              pathIarFeeFlat: this.state.pathIarFeeFlat,
              pathIarFeeTiered: this.state.pathIarFeeTiered,
            }}
            onChange={this.handleFieldChange}
          /></>}
        </CollapsibleSection>

        <CollapsibleSection
          sectionNumber="5"
          title="Review and Submit"
          isExpanded={expandedSection === "5"}
          isValid={this.isFormValid()}
          onToggle={() => this.toggleSection("5")}
        >
          <ISPConfirm
            state={{
              ...this.state,
              strategyDisplayName: this.getStrategyDisplayName(),
              pathStrategy: this.hasPath()
                ? this.getPathStrategyName()
                : "",
              hasPath: this.hasPath(),
              hasInitialFeeStage: this.hasInitialFeeStage(),
              bondTypePromptShown: this.isBondTypePromptShown(),
              bondTypePreferenceProvided:
                this.isBondTypePreferenceProvided(),
            }}
            lists={ispLists}
            isFormValid={this.isFormValid()}
            onSubmit={this.handleSubmit}
            isSubmitting={this.state.isSubmitting}
            submitError={this.state.submitError}
          />
        </CollapsibleSection>

        {/* Final-submit 0% IAR fee acknowledgment */}
        {this.state.showZeroFeeModal && (
            <div>
              <div className="modal-backdrop fade show" style={{ zIndex: 1050 }} />
              <div className="modal fade show" style={{ display: "block", zIndex: 1060 }} tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby="zeroFeeModalTitle" aria-describedby="zeroFeeModalMessage">
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title" id="zeroFeeModalTitle">
                        Confirm 0% IAR Fee
                      </h5>
                    </div>
                    <div className="modal-body">
                      <p className="mb-0" id="zeroFeeModalMessage">
                        {ZERO_FEE_CONFIRMATION_MESSAGE}
                      </p>
                    </div>
                    <div className="modal-footer zero-fee-modal-footer">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={this.handleCancelZeroFee}
                      >
                        Go Back and Edit Fees
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={this.handleConfirmZeroFee}
                        disabled={
                          this.state.zeroFeeConfirmationPending ||
                          this.state.isSubmitting
                        }
                      >
                        Confirm and Submit ISP
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        )}

        {/* Confirm Modal (delete advisor/client) */}
        {this.state.confirmModal && (
          <div>
            <div className="modal-backdrop fade show" style={{ zIndex: 1050 }} />
            <div className="modal fade show" style={{ display: "block", zIndex: 1060 }} tabIndex="-1" role="dialog" aria-modal="true">
              <div className="modal-dialog modal-dialog-centered modal-sm">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{this.state.confirmModal.title}</h5>
                  </div>
                  <div className="modal-body">
                    <p className="mb-0">{this.state.confirmModal.message}</p>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => this.setState({ confirmModal: null })}>
                      Cancel
                    </button>
                    <button type="button" className="btn btn-danger" onClick={this.state.confirmModal.onConfirm}>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Screen */}
        {this.state.showSuccess && (
          <div>
            <div
              className="modal-backdrop fade show"
              style={{ zIndex: 1050 }}
            />
            <div
              className="modal fade show"
              style={{ display: "block", zIndex: 1060 }}
              tabIndex="-1"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ispSuccessModalTitle"
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="ispSuccessModalTitle" style={{ color: "#385EA4" }}>
                      {this.state.isEditing
                        ? "Investment Strategy Plan Updated"
                        : "Investment Strategy Plan Submitted"}
                    </h5>
                  </div>
                  <div className="modal-body text-center py-4">
                    <div
                      style={{
                        fontSize: "48px",
                        color: "#28a745",
                        marginBottom: "10px",
                      }}
                    >
                      &#10003;
                    </div>
                    <h5>
                      {this.state.isEditing
                        ? "Your Investment Strategy Plan has been updated."
                        : "Your Investment Strategy Plan has been submitted."}
                    </h5>
                    <p className="text-muted">
                      {this.getStrategyDisplayName()} — {this.getCombinedClientName() || "Client"}
                    </p>
                    <p className="text-muted" style={{ fontSize: "0.9em" }}>
                      Documents are being generated. You can monitor their status on the submissions page.
                    </p>
                  </div>
                  <div className="modal-footer justify-content-center">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={this.handleCreateAnother}
                    >
                      Create Another ISP
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        if (this.props.updateProposalsState) {
                          this.props.updateProposalsState();
                        }
                        if (this.props.history) {
                          this.props.history.push("/submissions");
                        } else {
                          window.location.href = "/submissions";
                        }
                      }}
                    >
                      View Submissions
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default ISPForm;
